import type {
  DeploymentEnvironment,
  DeploymentRecord,
  DeploymentStatus,
  ReleaseArtifact,
  ReleaseArtifactType,
  ReleaseCandidate,
  ReleaseDefect,
  ReleaseDefectSeverity,
  ReleaseDefectStatus,
  ReleaseDecision,
  ReleaseGate,
  ReleaseGateCode,
  ReleaseGateStatus,
  ReleaseReadinessOverview,
  ReleaseSignoff,
  ReleaseSignoffRole,
  UatExecution,
  UatExecutionStatus,
  UatScenario
} from "./release-types";

import type {
  CrossModuleWorkflow,
  CrossModuleWorkflowStatus,
  MaintenanceCycle,
  MaintenanceLease,
  OperationalIncident,
  ProductionOverview,
  ReconciliationIssue,
  ReconciliationIssueStatus,
  ReconciliationRun,
  ReconciliationScope,
  ReliabilitySeverity,
  RepairJob,
  RepairJobStatus,
  RepairJobType,
  ServiceLevelSnapshot
} from "./production-types";

import type {
  DeviceCommand,
  DeviceConfigurationProfile,
  DeviceEnrollment,
  DeviceOperationsOverview,
  DevicePeripheral,
  DevicePrintJob,
  DeviceCommandType,
  DevicePeripheralType,
  DeviceTransport,
  ManagedDevice,
  ManagedDeviceClass,
  ManagedDevicePlatform,
  ManagedDeviceStatus,
  MobileSyncConflict,
  MobileSyncConflictStatus
} from "./device-operations-types";

import type {
  DataExchangeArtifact,
  DataExchangeJob,
  DeadLetter,
  EInvoiceReadiness,
  FiscalDocument,
  FiscalDocumentRevision,
  IntegrationConnector,
  IntegrationDeviceBinding,
  IntegrationMessage,
  IntegrationSecret,
  IntegrationOverview,
  TaxProfile,
  TrnValidation,
  WebhookEndpoint
} from "./integrations-types";


import type {
  BalanceSheet,
  BranchComparison,
  CashPaymentsAnalytics,
  CustomerAnalytics,
  EmployeeAnalytics,
  ExecutiveDashboard,
  ExpenseCategory,
  FinanceAccount,
  FinancialPeriod,
  Forecast,
  GeneralLedger,
  InventoryAnalytics,
  JournalEntry,
  OperatingExpense,
  ProductAnalytics,
  ProfitabilityAnalytics,
  ProfitAndLoss,
  PurchasingAnalytics,
  ReportDataset,
  ReportDefinition,
  ReportExport,
  ReportExportFormat,
  ReportRun,
  ReportSchedule,
  ReportOccurrence,
  ReportDeliveryResult,
  ReportScope,
  RestaurantAnalytics,
  SalesAnalytics,
  TaxVatReport,
  TrialBalance
} from "./reports-types";

import type { Campaign, CampaignStatus, Coupon, CouponStatus, CouponProduct, CouponPrint, CrmOverview, CustomerAccountSettlementResponse, CustomerConsent, CustomerListItem, CustomerNote, CustomerProfile, CustomerReceivableSummary, CustomerSegment, CustomerStatus, CustomerTag, GiftCard, GiftCardStatus, LoyaltyAccountSummary, LoyaltyProgram, LoyaltyTier, LoyaltyTransaction, SegmentRule, WalletAccountSummary, WalletTransaction } from "./crm-types";
import type {
  DeliveryDriver,
  DeliveryOrder,
  DriverStatus,
  Handover,
  HandoverRecipientType,
  HandoverStatus,
  KitchenBoard,
  KitchenPriority,
  KitchenRoutingRule,
  KitchenStation,
  KitchenTicket,
  Recipe,
  RecipeStatus,
  Reservation,
  ReservationStatus,
  RestaurantOverview,
  WaitlistEntry,
  WaitlistStatus
} from "./restaurant-types";
import type {
  GoodsReceipt,
  InventoryOverview,
  PurchaseOrder,
  PurchaseOrderDetail,
  PurchaseOrderLineInput,
  ReorderRule,
  StockBalance,
  Supplier,
  Warehouse
} from "./inventory-types";

import type {
  CashMovement,
  CashMovementType,
  Customer,
  DiningFloor,
  DiscountType,
  OpenOrderCard,
  OrderWorkflow,
  PaymentMethod,
  PaymentResult,
  PreBill,
  PreOrderDepositResult,
  PreOrderBalancePaymentResult,
  PreOrderFulfillmentResult,
  PreOrderProtectedActionResult,
  PreOrderCancellationDisposition,
  PreOrderDetail,
  PreOrderSummary,
  ScheduledOrderStatus,
  BookingReceipt,
  Receipt,
  Refund,
  ShiftSummary,
  TransactionSummary
} from "./cashier-types";

import type {
  CatalogOverview,
  Category,
  ModifierGroup,
  OrderCart,
  PosCatalog,
  PriceList,
  PriceListAssignment,
  PriceListEntry,
  Product,
  ProductStatus,
  Promotion,
  TaxCode,
  UnitOfMeasure
} from "./catalog-types";

import type {
  ApprovalPolicy,
  ApprovalQueueSummary,
  ApprovalRequestItem,
  ApprovalRequestStatus,
  ManagerApprovalGate,
  Employee,
  EmploymentStatus,
  MfaEnrollmentStart,
  PermissionGroup,
  RoleDetail,
  RoleSummary,
  SecurityOverview,
  SecuritySession,
  SecuritySessionStatus,
  UserAccess
} from "./identity-types";


import { LEGACY_STORAGE_KEYS, STORAGE_KEYS } from "../brand";

import type {
  Branch,
  BusinessHour,
  Device,
  OrganizationProfile,
  RuntimeSettings,
  SettingsCatalog,
  SettingsHistoryItem,
  SettingsOverview,
  SettingsScopeType,
  SettingsSectionState
} from "./settings-types";

import type {
  ContextOption,
  LoginResponse,
  Order,
  SelectContextResponse,
  ServiceType,
  Shift
} from "./types";

const TOKEN_KEY = STORAGE_KEYS.accessToken;
const CONTEXT_KEY = STORAGE_KEYS.selectedContext;
const PERMISSIONS_KEY = STORAGE_KEYS.permissions;
const PERMISSION_REVISION_KEY = STORAGE_KEYS.permissionRevision;
const MANAGER_APPROVAL_GATE_KEY = STORAGE_KEYS.managerApprovalGate;

function migrateLegacyStorageKey(
  storage: Storage,
  currentKey: string,
  legacyKey: string
): void {
  if (storage.getItem(currentKey) === null) {
    const legacyValue = storage.getItem(legacyKey);
    if (legacyValue !== null) {
      storage.setItem(currentKey, legacyValue);
    }
  }
  storage.removeItem(legacyKey);
}

migrateLegacyStorageKey(localStorage, TOKEN_KEY, LEGACY_STORAGE_KEYS.accessToken);
migrateLegacyStorageKey(localStorage, CONTEXT_KEY, LEGACY_STORAGE_KEYS.selectedContext);
migrateLegacyStorageKey(localStorage, PERMISSIONS_KEY, LEGACY_STORAGE_KEYS.permissions);
migrateLegacyStorageKey(
  localStorage,
  PERMISSION_REVISION_KEY,
  LEGACY_STORAGE_KEYS.permissionRevision
);
migrateLegacyStorageKey(
  sessionStorage,
  MANAGER_APPROVAL_GATE_KEY,
  LEGACY_STORAGE_KEYS.managerApprovalGate
);

export function createIdempotencyKey(): string {
  if (typeof crypto !== "undefined") {
    if (typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }

    if (typeof crypto.getRandomValues === "function") {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;

      const hex = Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, "0")
      );

      return [
        hex.slice(0, 4).join(""),
        hex.slice(4, 6).join(""),
        hex.slice(6, 8).join(""),
        hex.slice(8, 10).join(""),
        hex.slice(10, 16).join(""),
      ].join("-");
    }
  }

  return `leylion-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
export const SESSION_UPDATED_EVENT = "leylion-session-updated";
export const SESSION_CONTEXT_CHANGED_EVENT = "leylion-session-context-changed";
const sessionKeys: readonly string[] = [TOKEN_KEY, CONTEXT_KEY, PERMISSIONS_KEY];
const readSessionSnapshot = (): Record<string, string | null> =>
  Object.fromEntries(sessionKeys.map((key) => [key, localStorage.getItem(key)]));
let sessionSnapshot = readSessionSnapshot();
let sessionGeneration = 0;
let externalSessionChanged = false;
let sessionContextBoundaryInstalled = false;

function invalidateTabSession(): void {
  if (externalSessionChanged) return;
  externalSessionChanged = true;
  clearManagerApprovalGate();
  window.dispatchEvent(new CustomEvent(SESSION_CONTEXT_CHANGED_EVENT));
}

function sessionContextError(staleResponse = false): Error {
  const code = staleResponse ? "SESSION_CONTEXT_STALE_RESPONSE" : "SESSION_CONTEXT_CHANGED";
  return Object.assign(new Error(staleResponse
    ? "The session changed during this request. Check its result before retrying."
    : "The session changed in another tab. Reload this tab to continue."), {
    status: 409, problem: { title: code }, code
  });
}

export function hasSessionContextChanged(): boolean {
  return externalSessionChanged;
}

export function captureSessionGeneration(): number {
  if (sessionKeys.some((key) => localStorage.getItem(key) !== sessionSnapshot[key]))
    invalidateTabSession();
  if (externalSessionChanged) throw sessionContextError();
  return sessionGeneration;
}

export function assertSessionGenerationCurrent(expected: number): void {
  if (captureSessionGeneration() !== expected) throw sessionContextError(true);
}

function updateStoredSession(changes: Record<string, string | null>): void {
  captureSessionGeneration();
  const next = { ...sessionSnapshot, ...changes };
  for (const [key, value] of Object.entries(changes)) {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  }
  // Bind only to values this tab wrote, never adopt an interleaved foreign write.
  sessionSnapshot = next;
  sessionGeneration++;
  captureSessionGeneration();
  window.dispatchEvent(new CustomEvent(SESSION_UPDATED_EVENT));
}

export function installSessionContextBoundary(): void {
  if (sessionContextBoundaryInstalled) return;
  sessionContextBoundaryInstalled = true;
  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.storageArea !== localStorage || (event.key !== null && !sessionKeys.includes(event.key))) return;
    if (event.key === null || event.newValue !== sessionSnapshot[event.key]) invalidateTabSession();
  });
  window.addEventListener("focus", () => {
    if (sessionKeys.some((key) => localStorage.getItem(key) !== sessionSnapshot[key])) invalidateTabSession();
  });
}

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAccessToken(token: string): void {
  updateStoredSession({ [TOKEN_KEY]: token });
}

export function clearSession(): void {
  updateStoredSession({ [TOKEN_KEY]: null, [CONTEXT_KEY]: null, [PERMISSIONS_KEY]: null });
  clearManagerApprovalGate();
}

export function clearStoredContext(): void {
  updateStoredSession({ [CONTEXT_KEY]: null, [PERMISSIONS_KEY]: null });
  clearManagerApprovalGate();
}

export function clearManagerApprovalGate(): void {
  sessionStorage.removeItem(MANAGER_APPROVAL_GATE_KEY);
}

export function setStoredManagerApprovalGate(gate: ManagerApprovalGate): void {
  sessionStorage.setItem(MANAGER_APPROVAL_GATE_KEY, JSON.stringify(gate));
}

export function getStoredManagerApprovalGate(): ManagerApprovalGate | null {
  const raw = sessionStorage.getItem(MANAGER_APPROVAL_GATE_KEY);
  if (!raw) return null;

  try {
    const gate = JSON.parse(raw) as ManagerApprovalGate;
    if (!gate.accessToken || Number.isNaN(Date.parse(gate.expiresAt))
        || Date.parse(gate.expiresAt) <= Date.now()) {
      clearManagerApprovalGate();
      return null;
    }
    return gate;
  } catch {
    clearManagerApprovalGate();
    return null;
  }
}

export function prepareStoredContextForPermissionRevision(revision: string): void {
  if (localStorage.getItem(PERMISSION_REVISION_KEY) === revision) {
    return;
  }

  // Context JWTs carry a permission snapshot. A database grant cannot update an
  // already-issued token, so force one clean authentication after an RBAC repair.
  clearSession();
  localStorage.setItem(PERMISSION_REVISION_KEY, revision);
}

export function getStoredContext(): ContextOption | null {
  const raw = localStorage.getItem(CONTEXT_KEY);
  return raw ? (JSON.parse(raw) as ContextOption) : null;
}

export function setStoredContext(context: ContextOption): void {
  updateStoredSession({ [CONTEXT_KEY]: JSON.stringify(context) });
}

export function getStoredPermissions(): string[] {
  const raw = localStorage.getItem(PERMISSIONS_KEY);
  return raw ? (JSON.parse(raw) as string[]) : [];
}

export function setStoredPermissions(permissions: string[]): void {
  updateStoredSession({ [PERMISSIONS_KEY]: JSON.stringify(permissions) });
}

export async function request<T>(
  path: string,
  options: RequestInit = {},
  token: string | null = getAccessToken()
): Promise<T> {
  const sessionGeneration = captureSessionGeneration();
  const response = await fetch(path, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body && !(typeof FormData !== "undefined" && options.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {})
    }
  });

  assertSessionGenerationCurrent(sessionGeneration);
  if (response.status === 204) {
    return null as T;
  }

  const payload = await response.json().catch(() => ({}));
  assertSessionGenerationCurrent(sessionGeneration);
  if (!response.ok) {
    const error = new Error(payload.detail ?? payload.title ?? "Request failed.");
    Object.assign(error, { status: response.status, problem: payload });
    throw error;
  }

  return payload as T;
}

export function login(email: string, password: string, mfaCode?: string): Promise<LoginResponse> {
  return request<LoginResponse>(
    "/api/v1/auth/sessions",
    {
      method: "POST",
      body: JSON.stringify({ email, password, mfaCode: mfaCode || null })
    },
    null
  );
}

export function changeOwnPassword(
  token: string,
  currentPassword: string,
  newPassword: string
): Promise<LoginResponse> {
  return request<LoginResponse>(
    "/api/v1/auth/password",
    {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword })
    },
    token
  );
}

export async function getContextOptions(token: string): Promise<ContextOption[]> {
  const response = await request<{ items: ContextOption[] }>("/api/v1/context/options", {}, token);
  return response.items;
}

export function selectContext(
  token: string,
  tenantId: string,
  branchId: string
): Promise<SelectContextResponse> {
  return request<SelectContextResponse>(
    "/api/v1/context/select",
    {
      method: "POST",
      body: JSON.stringify({
        tenantId,
        branchId,
        deviceName: navigator.userAgent
      })
    },
    token
  );
}

export function getOpenShift(): Promise<Shift | null> {
  return request<Shift | null>("/api/v1/shifts/open");
}

export function openShift(openingCashMinor: number): Promise<Shift> {
  return request<Shift>("/api/v1/shifts/open", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ openingCashMinor })
  });
}

export async function getOpenOrders(): Promise<Order[]> {
  const response = await request<{ items: Order[] }>("/api/v1/orders/open");
  return response.items;
}

export async function getOpenOrderCards(): Promise<OpenOrderCard[]> {
  const response = await request<{ items: OpenOrderCard[] }>(
    "/api/v1/cashier/orders/open-cards"
  );
  return response.items;
}

export function getOrder(orderId: string): Promise<Order> {
  return request<Order>(`/api/v1/orders/${orderId}`);
}

export function createOrder(
  serviceType: ServiceType,
  note: string,
  operationId = createIdempotencyKey()
): Promise<Order> {
  return request<Order>("/api/v1/orders", {
    method: "POST",
    headers: { "Idempotency-Key": operationId },
    body: JSON.stringify({ serviceType, note: note || null })
  });
}

export function updateOrder(
  order: Order,
  operationId = createIdempotencyKey()
): Promise<Order> {
  return request<Order>(`/api/v1/orders/${order.id}`, {
    method: "PUT",
    headers: { "Idempotency-Key": operationId },
    body: JSON.stringify({
      serviceType: order.serviceType,
      note: order.note,
      version: order.version
    })
  });
}



export function createPreOrder(
  payload: {
    serviceType: ServiceType;
    customerId?: string | null;
    note: string | null;
    fulfillmentAt: string;
    preparationLeadMinutes: number | null;
    contactName: string | null;
    contactPhone: string | null;
    vehicleNumber: string | null;
    addressNotes: string | null;
    depositRequiredMinor: number;
  },
  operationId = createIdempotencyKey()
): Promise<PreOrderDetail> {
  return request<PreOrderDetail>("/api/v1/cashier/preorders", {
    method: "POST",
    headers: { "Idempotency-Key": operationId },
    body: JSON.stringify(payload)
  });
}

export async function searchPreOrders(filters: {
  search?: string;
  from?: string;
  to?: string;
  status?: ScheduledOrderStatus | "";
} = {}): Promise<PreOrderSummary[]> {
  const query = new URLSearchParams();
  if (filters.search) query.set("search", filters.search);
  if (filters.from) query.set("from", filters.from);
  if (filters.to) query.set("to", filters.to);
  if (filters.status) query.set("status", filters.status);
  const response = await request<{ items: PreOrderSummary[] }>(
    `/api/v1/cashier/preorders${query.toString() ? `?${query.toString()}` : ""}`
  );
  return response.items;
}

export function getPreOrder(orderId: string): Promise<PreOrderDetail> {
  return request<PreOrderDetail>(`/api/v1/cashier/preorders/${orderId}`);
}

export function updatePreOrder(
  orderId: string,
  payload: {
    fulfillmentAt: string;
    customerSelection?: { customerId: string | null };
    preparationLeadMinutes: number;
    depositRequiredMinor: number;
    notes: string | null;
    contactName: string | null;
    contactPhone: string | null;
    vehicleNumber: string | null;
    addressNotes: string | null;
    expectedOrderVersion: number;
    expectedScheduledOrderVersion: number;
  },
  operationId = createIdempotencyKey()
): Promise<PreOrderDetail> {
  return request<PreOrderDetail>(`/api/v1/cashier/preorders/${orderId}`, {
    method: "PUT",
    headers: { "Idempotency-Key": operationId },
    body: JSON.stringify(payload)
  });
}

export function confirmPreOrder(
  orderId: string,
  expectedOrderVersion: number,
  expectedScheduledOrderVersion: number,
  operationId = createIdempotencyKey()
): Promise<PreOrderDetail> {
  return request<PreOrderDetail>(
    `/api/v1/cashier/preorders/${orderId}/confirm`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({ expectedOrderVersion, expectedScheduledOrderVersion })
    }
  );
}

export function capturePreOrderDeposit(
  orderId: string,
  payload: {
    giftCardCode?: string;
    giftCardCurrency?: string;
    method: PaymentMethod;
    amountMinor: number;
    tenderedMinor: number;
    provider: string | null;
    providerReference: string | null;
    clientOperationId: string;
    expectedOrderVersion: number;
    expectedScheduledOrderVersion: number;
  },
  operationId = createIdempotencyKey()
): Promise<PreOrderDepositResult> {
  return request<PreOrderDepositResult>(
    `/api/v1/cashier/preorders/${orderId}/payments/deposit`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify(payload)
    }
  );
}

export function releasePreOrderToKitchen(
  orderId: string,
  expectedOrderVersion: number,
  expectedScheduledOrderVersion: number,
  note: string | null = null,
  operationId = createIdempotencyKey()
): Promise<PreOrderDetail> {
  return request<PreOrderDetail>(
    `/api/v1/cashier/preorders/${orderId}/release-to-kitchen`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({ expectedOrderVersion, expectedScheduledOrderVersion, note })
    }
  );
}

export function capturePreOrderBalance(
  orderId: string,
  payload: {
    giftCardCode?: string;
    giftCardCurrency?: string;
    method: PaymentMethod;
    amountMinor: number;
    tenderedMinor: number;
    provider: string | null;
    providerReference: string | null;
    clientOperationId: string;
    expectedOrderVersion: number;
    expectedScheduledOrderVersion: number;
  },
  operationId = createIdempotencyKey()
): Promise<PreOrderBalancePaymentResult> {
  return request<PreOrderBalancePaymentResult>(
    `/api/v1/cashier/preorders/${orderId}/payments/balance`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify(payload)
    }
  );
}

export function fulfillPreOrder(
  orderId: string,
  expectedOrderVersion: number,
  expectedScheduledOrderVersion: number,
  handoverNote: string | null = null,
  operationId = createIdempotencyKey()
): Promise<PreOrderFulfillmentResult> {
  return request<PreOrderFulfillmentResult>(
    `/api/v1/cashier/preorders/${orderId}/fulfill`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({ expectedOrderVersion, expectedScheduledOrderVersion, handoverNote })
    }
  );
}

export function reschedulePreOrder(
  orderId: string,
  payload: {
    fulfillmentAt: string;
    preparationLeadMinutes: number;
    reason: string;
    expectedOrderVersion: number;
    expectedScheduledOrderVersion: number;
  },
  operationId = createIdempotencyKey()
): Promise<PreOrderProtectedActionResult> {
  return request<PreOrderProtectedActionResult>(
    `/api/v1/cashier/preorders/${orderId}/reschedule`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify(payload)
    }
  );
}

export function cancelPreOrder(
  orderId: string,
  payload: {
    reason: string;
    disposition: PreOrderCancellationDisposition;
    depositRefundMinor: number | null;
    expectedOrderVersion: number;
    expectedScheduledOrderVersion: number;
  },
  operationId = createIdempotencyKey()
): Promise<PreOrderProtectedActionResult> {
  return request<PreOrderProtectedActionResult>(
    `/api/v1/cashier/preorders/${orderId}/cancel`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify(payload)
    }
  );
}

export function generateBookingReceipt(
  orderId: string,
  expectedOrderVersion: number,
  expectedScheduledOrderVersion: number,
  operationId = createIdempotencyKey()
): Promise<BookingReceipt> {
  return request<BookingReceipt>(
    `/api/v1/cashier/preorders/${orderId}/booking-receipt`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({ expectedOrderVersion, expectedScheduledOrderVersion })
    }
  );
}

export function getSettingsCatalog(): Promise<SettingsCatalog> {
  return request<SettingsCatalog>("/api/v1/settings/catalog");
}

export function getSettingsOverview(
  scopeType: SettingsScopeType,
  scopeId: string
): Promise<SettingsOverview> {
  return request<SettingsOverview>(
    `/api/v1/settings/overview?scopeType=${encodeURIComponent(scopeType)}&scopeId=${encodeURIComponent(scopeId)}`
  );
}

export function getSettingsSection(
  sectionKey: string,
  scopeType: SettingsScopeType,
  scopeId: string
): Promise<SettingsSectionState> {
  return request<SettingsSectionState>(
    `/api/v1/settings/sections/${encodeURIComponent(sectionKey)}?scopeType=${encodeURIComponent(scopeType)}&scopeId=${encodeURIComponent(scopeId)}`
  );
}

export function saveSettingsDraft(
  sectionKey: string,
  scopeType: SettingsScopeType,
  scopeId: string,
  values: Record<string, unknown>,
  expectedVersion?: number
): Promise<SettingsSectionState> {
  return request<SettingsSectionState>(
    `/api/v1/settings/sections/${encodeURIComponent(sectionKey)}/draft`,
    {
      method: "PUT",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({
        scopeType,
        scopeId,
        values,
        expectedVersion: expectedVersion ?? null
      })
    }
  );
}

export function publishSettings(
  sectionKey: string,
  scopeType: SettingsScopeType,
  scopeId: string,
  expectedVersion: number
): Promise<SettingsSectionState> {
  return request<SettingsSectionState>(
    `/api/v1/settings/sections/${encodeURIComponent(sectionKey)}/publish`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({ scopeType, scopeId, expectedVersion })
    }
  );
}

export function discardSettingsDraft(
  sectionKey: string,
  scopeType: SettingsScopeType,
  scopeId: string
): Promise<null> {
  return request<null>(
    `/api/v1/settings/sections/${encodeURIComponent(sectionKey)}/draft?scopeType=${encodeURIComponent(scopeType)}&scopeId=${encodeURIComponent(scopeId)}`,
    { method: "DELETE" }
  );
}

export async function getSettingsHistory(
  scopeType?: SettingsScopeType,
  scopeId?: string,
  sectionKey?: string
): Promise<SettingsHistoryItem[]> {
  const query = new URLSearchParams();
  if (scopeType) query.set("scopeType", scopeType);
  if (scopeId) query.set("scopeId", scopeId);
  if (sectionKey) query.set("sectionKey", sectionKey);
  const response = await request<{ items: SettingsHistoryItem[] }>(
    `/api/v1/settings/history?${query.toString()}`
  );
  return response.items;
}

export function getOrganizationProfile(): Promise<OrganizationProfile> {
  return request<OrganizationProfile>("/api/v1/organization/profile");
}

export function updateOrganizationProfile(
  profile: OrganizationProfile
): Promise<OrganizationProfile> {
  return request<OrganizationProfile>("/api/v1/organization/profile", {
    method: "PUT",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      tradeName: profile.tradeName,
      legalName: profile.legalName,
      licenseNumber: profile.licenseNumber,
      taxRegistrationNumber: profile.taxRegistrationNumber,
      countryCode: profile.countryCode,
      email: profile.email,
      phone: profile.phone,
      website: profile.website,
      addressLine1: profile.addressLine1,
      addressLine2: profile.addressLine2,
      city: profile.city,
      region: profile.region,
      postalCode: profile.postalCode,
      logoUrl: profile.logoUrl,
      expectedVersion: profile.version
    })
  });
}

export async function getBranches(): Promise<Branch[]> {
  const response = await request<{ items: Branch[] }>("/api/v1/branches");
  return response.items;
}

export function createBranch(branch: Omit<Branch, "id" | "tenantId" | "version" | "updatedAt">): Promise<Branch> {
  return request<Branch>("/api/v1/branches", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ ...branch, expectedVersion: null })
  });
}

export function updateBranch(branch: Branch): Promise<Branch> {
  return request<Branch>(`/api/v1/branches/${branch.id}`, {
    method: "PUT",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      name: branch.name,
      code: branch.code,
      locationType: branch.locationType,
      isActive: branch.isActive,
      countryCode: branch.countryCode,
      region: branch.region,
      city: branch.city,
      addressLine1: branch.addressLine1,
      addressLine2: branch.addressLine2,
      postalCode: branch.postalCode,
      phone: branch.phone,
      email: branch.email,
      timeZoneOverride: branch.timeZoneOverride,
      currencyOverride: branch.currencyOverride,
      taxRegistrationNumberOverride: branch.taxRegistrationNumberOverride,
      businessDayCutoffMinute: branch.businessDayCutoffMinute,
      latitude: branch.latitude,
      longitude: branch.longitude,
      expectedVersion: branch.version
    })
  });
}

export async function getBusinessHours(branchId: string): Promise<BusinessHour[]> {
  const response = await request<{ items: BusinessHour[] }>(
    `/api/v1/branches/${branchId}/business-hours`
  );
  return response.items;
}

export async function updateBusinessHours(
  branchId: string,
  hours: BusinessHour[]
): Promise<BusinessHour[]> {
  const response = await request<{ items: BusinessHour[] }>(
    `/api/v1/branches/${branchId}/business-hours`,
    {
      method: "PUT",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify(
        hours.map((hour) => ({
          dayOfWeek: hour.dayOfWeek,
          isClosed: hour.isClosed,
          openMinute: hour.openMinute,
          closeMinute: hour.closeMinute,
          spansNextDay: hour.spansNextDay,
          expectedVersion: hour.version ?? null
        }))
      )
    }
  );
  return response.items;
}

export async function getDevices(branchId?: string): Promise<Device[]> {
  const response = await request<{ items: Device[] }>(
    `/api/v1/devices${branchId ? `?branchId=${encodeURIComponent(branchId)}` : ""}`
  );
  return response.items;
}

export function getRuntimeSettings(
  branchId: string,
  deviceId?: string
): Promise<RuntimeSettings> {
  const query = new URLSearchParams({ branchId });
  if (deviceId) query.set("deviceId", deviceId);
  return request<RuntimeSettings>(`/api/v1/settings/runtime?${query.toString()}`);
}

export async function exportEffectiveSettings(
  branchId: string,
  deviceId?: string
): Promise<void> {
  const query = new URLSearchParams({ branchId });
  if (deviceId) query.set("deviceId", deviceId);
  const payload = await request<Record<string, unknown>>(
    `/api/v1/settings/effective?${query.toString()}`
  );
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json"
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `leylion-effective-settings-${branchId}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}


export function getSecurityOverview(): Promise<SecurityOverview> {
  return request<SecurityOverview>("/api/v1/security/overview");
}

export async function getPermissionGroups(): Promise<PermissionGroup[]> {
  const response = await request<{ groups: PermissionGroup[] }>(
    "/api/v1/security/permissions"
  );
  return response.groups;
}

export async function getRoles(): Promise<RoleSummary[]> {
  const response = await request<{ items: RoleSummary[] }>("/api/v1/security/roles");
  return response.items;
}

export function getRole(roleId: string): Promise<RoleDetail> {
  return request<RoleDetail>(`/api/v1/security/roles/${roleId}`);
}

export function createRole(input: {
  code: string;
  nameAr: string;
  nameEn: string;
  descriptionAr: string | null;
  descriptionEn: string | null;
  isActive: boolean;
  permissions: string[];
}): Promise<RoleDetail> {
  return request<RoleDetail>("/api/v1/security/roles", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ ...input, expectedVersion: null })
  });
}

export function updateRole(detail: RoleDetail): Promise<RoleDetail> {
  return request<RoleDetail>(`/api/v1/security/roles/${detail.role.id}`, {
    method: "PUT",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      code: detail.role.code,
      nameAr: detail.role.nameAr,
      nameEn: detail.role.nameEn,
      descriptionAr: detail.role.descriptionAr,
      descriptionEn: detail.role.descriptionEn,
      isActive: detail.role.isActive,
      permissions: detail.permissions,
      expectedVersion: detail.role.version
    })
  });
}

export async function getEmployees(
  search?: string,
  status?: EmploymentStatus,
  branchId?: string
): Promise<Employee[]> {
  const query = new URLSearchParams();
  if (search) query.set("search", search);
  if (status) query.set("status", status);
  if (branchId) query.set("branchId", branchId);
  const response = await request<{ items: Employee[] }>(
    `/api/v1/employees?${query.toString()}`
  );
  return response.items;
}

export function getEmployee(employeeId: string): Promise<Employee> {
  return request<Employee>(`/api/v1/employees/${employeeId}`);
}

export function saveEmployee(
  employeeId: string | null,
  input: Record<string, unknown>
): Promise<Employee> {
  return request<Employee>(
    employeeId ? `/api/v1/employees/${employeeId}` : "/api/v1/employees",
    {
      method: employeeId ? "PUT" : "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify(input)
    }
  );
}

export async function getUsers(): Promise<UserAccess[]> {
  const response = await request<{ items: UserAccess[] }>("/api/v1/users");
  return response.items;
}

export function setUserStatus(
  user: UserAccess,
  isActive: boolean
): Promise<UserAccess> {
  return request<UserAccess>(`/api/v1/users/${user.userId}/status`, {
    method: "PUT",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ isActive, expectedVersion: user.version })
  });
}

export function assignUserRole(
  userId: string,
  input: {
    roleId: string;
    branchId: string;
    isActive: boolean;
    validFrom: string | null;
    validUntil: string | null;
    expectedVersion?: number;
  }
): Promise<UserAccess["assignments"][number]> {
  return request<UserAccess["assignments"][number]>(
    `/api/v1/users/${userId}/role-assignments`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({ ...input, expectedVersion: input.expectedVersion ?? null })
    }
  );
}

export function deleteUserRoleAssignment(
  userId: string,
  assignmentId: string
): Promise<null> {
  return request<null>(
    `/api/v1/users/${userId}/role-assignments/${assignmentId}`,
    { method: "DELETE" }
  );
}

export function resetUserPassword(
  userId: string,
  temporaryPassword: string,
  mustChangePassword = true
): Promise<null> {
  return request<null>(`/api/v1/users/${userId}/password-reset`, {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ temporaryPassword, mustChangePassword })
  });
}

export function resetUserPin(userId: string, pin: string): Promise<null> {
  return request<null>(`/api/v1/users/${userId}/pin-reset`, {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ pin })
  });
}

export function startMfaEnrollment(userId: string): Promise<MfaEnrollmentStart> {
  return request<MfaEnrollmentStart>(
    `/api/v1/users/${userId}/mfa/enrollment`,
    { method: "POST" }
  );
}

export function verifyMfaEnrollment(
  userId: string,
  enrollment: MfaEnrollmentStart,
  code: string
): Promise<null> {
  return request<null>(`/api/v1/users/${userId}/mfa/verify`, {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      code,
      secret: enrollment.secret,
      recoveryCodes: enrollment.recoveryCodes
    })
  });
}

export function resetUserMfa(userId: string, reason: string): Promise<null> {
  return request<null>(`/api/v1/users/${userId}/mfa/reset`, {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ reason })
  });
}

export async function getSecuritySessions(
  userId?: string,
  status?: SecuritySessionStatus
): Promise<SecuritySession[]> {
  const query = new URLSearchParams();
  if (userId) query.set("userId", userId);
  if (status) query.set("status", status);
  const response = await request<{ items: SecuritySession[] }>(
    `/api/v1/security/sessions?${query.toString()}`
  );
  return response.items;
}

export function revokeSecuritySession(
  sessionId: string,
  reason: string
): Promise<null> {
  return request<null>(`/api/v1/security/sessions/${sessionId}/revoke`, {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ reason })
  });
}

export async function getApprovalPolicies(): Promise<ApprovalPolicy[]> {
  const response = await request<{ items: ApprovalPolicy[] }>(
    "/api/v1/approvals/policies"
  );
  return response.items;
}

export function saveApprovalPolicy(
  policy: Partial<ApprovalPolicy> & {
    operation: ApprovalPolicy["operation"];
    requiredPermission: string;
    expiresAfterMinutes: number;
    requireReason: boolean;
    isEnabled: boolean;
  }
): Promise<ApprovalPolicy> {
  const url = policy.id
    ? `/api/v1/approvals/policies/${policy.id}`
    : "/api/v1/approvals/policies";
  return request<ApprovalPolicy>(url, {
    method: policy.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      branchId: policy.branchId ?? null,
      operation: policy.operation,
      isEnabled: policy.isEnabled,
      thresholdMinor: policy.thresholdMinor ?? null,
      thresholdBasisPoints: policy.thresholdBasisPoints ?? null,
      requiredPermission: policy.requiredPermission,
      expiresAfterMinutes: policy.expiresAfterMinutes,
      requireReason: policy.requireReason,
      expectedVersion: policy.version ?? null
    })
  });
}

export function verifyManagerApprovalPin(pin: string): Promise<ManagerApprovalGate> {
  return request<ManagerApprovalGate>("/api/v1/approvals/manager-access/verify", {
    method: "POST",
    body: JSON.stringify({ pin })
  });
}

export function getApprovalQueueSummary(): Promise<ApprovalQueueSummary> {
  return request<ApprovalQueueSummary>("/api/v1/approvals/requests/summary");
}

export async function getApprovalRequests(
  managerAccessToken: string,
  status?: ApprovalRequestStatus
): Promise<ApprovalRequestItem[]> {
  const response = await request<{ items: ApprovalRequestItem[] }>(
    `/api/v1/approvals/requests${status ? `?status=${status}` : ""}`,
    { headers: { "X-Manager-Approval-Token": managerAccessToken } }
  );
  return response.items;
}

export function decideApprovalRequest(
  requestItem: ApprovalRequestItem,
  approve: boolean,
  decisionReason: string,
  managerAccessToken: string
): Promise<ApprovalRequestItem> {
  return request<ApprovalRequestItem>(
    `/api/v1/approvals/requests/${requestItem.id}/decision`,
    {
      method: "POST",
      headers: {
        "Idempotency-Key": createIdempotencyKey(),
        "X-Manager-Approval-Token": managerAccessToken
      },
      body: JSON.stringify({
        approve,
        decisionReason: decisionReason || null,
        expectedVersion: requestItem.version
      })
    }
  );
}


export function createRefundApprovalRequest(
  workflow: OrderWorkflow,
  reason: string,
  lines: Array<{ originalOrderLineId: string; quantityMilli: number }>
): Promise<ApprovalRequestItem> {
  return request<ApprovalRequestItem>("/api/v1/approvals/requests/refund", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      orderId: workflow.order.id,
      expectedOrderVersion: workflow.order.version,
      reason,
      lines
    })
  });
}

export function createVoidApprovalRequest(
  workflow: OrderWorkflow,
  reason: string
): Promise<ApprovalRequestItem> {
  return request<ApprovalRequestItem>("/api/v1/approvals/requests/void", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      orderId: workflow.order.id,
      expectedOrderVersion: workflow.order.version,
      reason
    })
  });
}

export function executeApprovalRequest(
  requestItem: ApprovalRequestItem,
  managerAccessToken: string,
  giftCardReplacement?: GiftCardReplacement
): Promise<ApprovalRequestItem> {
  return request<ApprovalRequestItem>(
    `/api/v1/approvals/requests/${requestItem.id}/execute`,
    {
      method: "POST",
      body: giftCardReplacement ? JSON.stringify(giftCardReplacement) : undefined,
      headers: {
        "Idempotency-Key": createIdempotencyKey(),
        "X-Manager-Approval-Token": managerAccessToken
      }
    }
  );
}


export function getCatalogOverview(): Promise<CatalogOverview> {
  return request<CatalogOverview>("/api/v1/catalog/overview");
}

export async function getCategories(): Promise<Category[]> {
  const response = await request<{ items: Category[] }>("/api/v1/catalog/categories");
  return response.items;
}

export function saveCategory(category: Partial<Category> & {
  code: string;
  nameAr: string;
  nameEn: string;
}): Promise<Category> {
  const url = category.id
    ? `/api/v1/catalog/categories/${category.id}`
    : "/api/v1/catalog/categories";
  return request<Category>(url, {
    method: category.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      parentCategoryId: category.parentCategoryId ?? null,
      code: category.code,
      nameAr: category.nameAr,
      nameEn: category.nameEn,
      descriptionAr: category.descriptionAr ?? null,
      descriptionEn: category.descriptionEn ?? null,
      imageUrl: category.imageUrl ?? null,
      sortOrder: category.sortOrder ?? 0,
      isActive: category.isActive ?? true,
      expectedVersion: category.version ?? null
    })
  });
}

export async function getUnits(): Promise<UnitOfMeasure[]> {
  const response = await request<{ items: UnitOfMeasure[] }>("/api/v1/catalog/units");
  return response.items;
}

export function saveUnit(unit: Partial<UnitOfMeasure> & {
  code: string;
  nameAr: string;
  nameEn: string;
}): Promise<UnitOfMeasure> {
  const url = unit.id ? `/api/v1/catalog/units/${unit.id}` : "/api/v1/catalog/units";
  return request<UnitOfMeasure>(url, {
    method: unit.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      code: unit.code,
      nameAr: unit.nameAr,
      nameEn: unit.nameEn,
      symbolAr: unit.symbolAr ?? null,
      symbolEn: unit.symbolEn ?? null,
      precision: unit.precision ?? 0,
      isFractional: unit.isFractional ?? false,
      isActive: unit.isActive ?? true,
      expectedVersion: unit.version ?? null
    })
  });
}

export async function getTaxCodes(): Promise<TaxCode[]> {
  const response = await request<{ items: TaxCode[] }>("/api/v1/catalog/tax-codes");
  return response.items;
}

export function saveTaxCode(tax: Partial<TaxCode> & {
  code: string;
  nameAr: string;
  nameEn: string;
  treatment: TaxCode["treatment"];
  rateBasisPoints: number;
}): Promise<TaxCode> {
  const url = tax.id
    ? `/api/v1/catalog/tax-codes/${tax.id}`
    : "/api/v1/catalog/tax-codes";
  return request<TaxCode>(url, {
    method: tax.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      code: tax.code,
      nameAr: tax.nameAr,
      nameEn: tax.nameEn,
      treatment: tax.treatment,
      rateBasisPoints: tax.rateBasisPoints,
      reportingCode: tax.reportingCode ?? null,
      isDefault: tax.isDefault ?? false,
      isActive: tax.isActive ?? true,
      effectiveFrom: tax.effectiveFrom ?? new Date().toISOString(),
      effectiveUntil: tax.effectiveUntil ?? null,
      expectedVersion: tax.version ?? null
    })
  });
}

export async function getProducts(
  search?: string,
  categoryId?: string,
  status?: ProductStatus
): Promise<Product[]> {
  const query = new URLSearchParams();
  if (search) query.set("search", search);
  if (categoryId) query.set("categoryId", categoryId);
  if (status) query.set("status", status);
  const response = await request<{ items: Product[] }>(
    `/api/v1/catalog/products?${query.toString()}`
  );
  return response.items;
}

export type ProductImageUploadResult = {
  imageUrl: string;
  contentType: string;
  sizeBytes: number;
};

export function uploadProductImage(file: File): Promise<ProductImageUploadResult> {
  const body = new FormData();
  body.append("file", file, file.name);
  return request<ProductImageUploadResult>("/api/v1/catalog/product-images", {
    method: "POST",
    body
  });
}

export function deleteProductImage(imageUrl: string): Promise<void> {
  const query = new URLSearchParams({ imageUrl });
  return request<void>(`/api/v1/catalog/product-images?${query.toString()}`, {
    method: "DELETE"
  });
}

export function getProduct(productId: string): Promise<Product> {
  return request<Product>(`/api/v1/catalog/products/${productId}`);
}

export function saveProduct(product: Partial<Product> & {
  categoryId: string;
  unitOfMeasureId: string;
  taxCodeId: string;
  code: string;
  nameAr: string;
  nameEn: string;
  productType: Product["productType"];
  status: Product["status"];
  variants: Product["variants"];
}): Promise<Product> {
  const url = product.id
    ? `/api/v1/catalog/products/${product.id}`
    : "/api/v1/catalog/products";
  return request<Product>(url, {
    method: product.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      categoryId: product.categoryId,
      unitOfMeasureId: product.unitOfMeasureId,
      taxCodeId: product.taxCodeId,
      code: product.code,
      nameAr: product.nameAr,
      nameEn: product.nameEn,
      descriptionAr: product.descriptionAr ?? null,
      descriptionEn: product.descriptionEn ?? null,
      productType: product.productType,
      status: product.status,
      imageUrl: product.imageUrl ?? null,
      trackInventory: product.trackInventory ?? false,
      allowOpenPrice: product.allowOpenPrice ?? false,
      sellable: product.sellable ?? true,
      purchasable: product.purchasable ?? false,
      isFavorite: product.isFavorite ?? false,
      sortOrder: product.sortOrder ?? 0,
      modifierGroupIds: product.modifierGroupIds ?? [],
      variants: product.variants.map((variant) => ({
        id: variant.id ?? null,
        sku: variant.sku,
        nameAr: variant.nameAr,
        nameEn: variant.nameEn,
        attributesJson: variant.attributesJson ?? null,
        costMinor: variant.costMinor ?? null,
        isDefault: variant.isDefault,
        isActive: variant.isActive,
        sortOrder: variant.sortOrder,
        barcodes: variant.barcodes.map((barcode) => ({
          id: barcode.id ?? null,
          barcode: barcode.barcode,
          barcodeType: barcode.barcodeType,
          isPrimary: barcode.isPrimary
        }))
      })),
      expectedVersion: product.version ?? null
    })
  });
}

export async function getModifierGroups(): Promise<ModifierGroup[]> {
  const response = await request<{ items: ModifierGroup[] }>(
    "/api/v1/catalog/modifier-groups"
  );
  return response.items;
}

export function saveModifierGroup(group: Partial<ModifierGroup> & {
  code: string;
  nameAr: string;
  nameEn: string;
  options: ModifierGroup["options"];
}): Promise<ModifierGroup> {
  const url = group.id
    ? `/api/v1/catalog/modifier-groups/${group.id}`
    : "/api/v1/catalog/modifier-groups";
  return request<ModifierGroup>(url, {
    method: group.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      code: group.code,
      nameAr: group.nameAr,
      nameEn: group.nameEn,
      minimumSelections: group.minimumSelections ?? 0,
      maximumSelections: group.maximumSelections ?? 1,
      isRequired: group.isRequired ?? false,
      isActive: group.isActive ?? true,
      sortOrder: group.sortOrder ?? 0,
      options: group.options.map((option) => ({
        id: option.id ?? null,
        code: option.code,
        nameAr: option.nameAr,
        nameEn: option.nameEn,
        priceDeltaMinor: option.priceDeltaMinor,
        taxCodeId: option.taxCodeId ?? null,
        isDefault: option.isDefault,
        isActive: option.isActive,
        sortOrder: option.sortOrder
      })),
      expectedVersion: group.version ?? null
    })
  });
}

export async function getPriceLists(): Promise<PriceList[]> {
  const response = await request<{ items: PriceList[] }>("/api/v1/pricing/price-lists");
  return response.items;
}

export function savePriceList(priceList: Partial<PriceList> & {
  code: string;
  nameAr: string;
  nameEn: string;
  scope: PriceList["scope"];
  currency: string;
}): Promise<PriceList> {
  const url = priceList.id
    ? `/api/v1/pricing/price-lists/${priceList.id}`
    : "/api/v1/pricing/price-lists";
  return request<PriceList>(url, {
    method: priceList.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      code: priceList.code,
      nameAr: priceList.nameAr,
      nameEn: priceList.nameEn,
      scope: priceList.scope,
      currency: priceList.currency,
      pricesIncludeTax: priceList.pricesIncludeTax ?? true,
      isDefault: priceList.isDefault ?? false,
      isActive: priceList.isActive ?? true,
      priority: priceList.priority ?? 0,
      effectiveFrom: priceList.effectiveFrom ?? new Date().toISOString(),
      effectiveUntil: priceList.effectiveUntil ?? null,
      expectedVersion: priceList.version ?? null
    })
  });
}

export async function getPriceListEntries(priceListId: string): Promise<PriceListEntry[]> {
  const response = await request<{ items: PriceListEntry[] }>(
    `/api/v1/pricing/price-lists/${priceListId}/entries`
  );
  return response.items;
}

export function savePriceListEntry(
  priceListId: string,
  entry: Partial<PriceListEntry> & {
    productVariantId: string;
    unitPriceMinor: number;
  }
): Promise<PriceListEntry> {
  const url = entry.id
    ? `/api/v1/pricing/price-lists/${priceListId}/entries/${entry.id}`
    : `/api/v1/pricing/price-lists/${priceListId}/entries`;
  return request<PriceListEntry>(url, {
    method: entry.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      productVariantId: entry.productVariantId,
      unitPriceMinor: entry.unitPriceMinor,
      compareAtPriceMinor: entry.compareAtPriceMinor ?? null,
      effectiveFrom: entry.effectiveFrom ?? new Date().toISOString(),
      effectiveUntil: entry.effectiveUntil ?? null,
      expectedVersion: entry.version ?? null
    })
  });
}

export async function getPriceListAssignments(): Promise<PriceListAssignment[]> {
  return (await request<{ items: PriceListAssignment[] }>("/api/v1/pricing/assignments")).items;
}

export function assignPriceList(input: {
  branchId: string;
  priceListId: string;
  priority: number;
  isActive: boolean;
  effectiveFrom: string;
  effectiveUntil: string | null;
}): Promise<PriceListAssignment> {
  return request<PriceListAssignment>("/api/v1/pricing/assignments", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify(input)
  });
}

export async function getPromotions(): Promise<Promotion[]> {
  const response = await request<{ items: Promotion[] }>("/api/v1/pricing/promotions");
  return response.items;
}

export function savePromotion(promotion: Partial<Promotion> & {
  code: string;
  nameAr: string;
  nameEn: string;
  promotionType: Promotion["promotionType"];
  status: Promotion["status"];
  targetType: Promotion["targetType"];
}): Promise<Promotion> {
  const url = promotion.id
    ? `/api/v1/pricing/promotions/${promotion.id}`
    : "/api/v1/pricing/promotions";
  return request<Promotion>(url, {
    method: promotion.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      code: promotion.code,
      nameAr: promotion.nameAr,
      nameEn: promotion.nameEn,
      promotionType: promotion.promotionType,
      status: promotion.status,
      targetType: promotion.targetType,
      targetId: promotion.targetId ?? null,
      discountBasisPoints: promotion.discountBasisPoints ?? null,
      discountAmountMinor: promotion.discountAmountMinor ?? null,
      buyQuantity: promotion.buyQuantity ?? null,
      getQuantity: promotion.getQuantity ?? null,
      maximumDiscountMinor: promotion.maximumDiscountMinor ?? null,
      minimumSpendMinor: promotion.minimumSpendMinor ?? null,
      enabledServiceTypes: promotion.enabledServiceTypes ?? [
        "WalkIn",
        "DineIn",
        "Takeaway",
        "Delivery"
      ],
      isCombinable: promotion.isCombinable ?? false,
      priority: promotion.priority ?? 0,
      startsAt: promotion.startsAt ?? new Date().toISOString(),
      endsAt:
        promotion.endsAt ??
        new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      usageLimit: promotion.usageLimit ?? null,
      expectedVersion: promotion.version ?? null
    })
  });
}

export function getPosCatalog(branchId: string): Promise<PosCatalog> {
  return request<PosCatalog>(
    `/api/v1/pos/catalog?branchId=${encodeURIComponent(branchId)}`
  );
}

export function getOrderCart(orderId: string): Promise<OrderCart> {
  return request<OrderCart>(`/api/v1/orders/${orderId}/cart`);
}

export function addOrderLine(
  orderId: string,
  productVariantId: string,
  modifierOptionIds: string[],
  expectedOrderVersion: number,
  manualUnitPriceMinor?: number | null,
  quantityMilli = 1000,
  note: string | null = null,
  operationId = createIdempotencyKey()
): Promise<OrderCart> {
  return request<OrderCart>(`/api/v1/orders/${orderId}/cart/lines`, {
    method: "POST",
    headers: { "Idempotency-Key": operationId },
    body: JSON.stringify({
      productVariantId,
      quantityMilli,
      modifierOptionIds,
      manualUnitPriceMinor: manualUnitPriceMinor ?? null,
      note,
      expectedOrderVersion
    })
  });
}

export function updateOrderLine(
  orderId: string,
  lineId: string,
  quantityMilli: number,
  modifierOptionIds: string[],
  expectedLineVersion: number,
  expectedOrderVersion: number,
  manualUnitPriceMinor: number | null = null,
  note: string | null = null,
  operationId = createIdempotencyKey()
): Promise<OrderCart> {
  return request<OrderCart>(
    `/api/v1/orders/${orderId}/cart/lines/${lineId}`,
    {
      method: "PUT",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({
        quantityMilli,
        modifierOptionIds,
        manualUnitPriceMinor,
        note,
        expectedLineVersion,
        expectedOrderVersion
      })
    }
  );
}

export function applyPreOrderLineReductionOverride(
  orderId: string,
  lineId: string,
  payload: {
    quantityMilli: number;
    modifierOptionIds: string[];
    manualUnitPriceMinor: number | null;
    note: string | null;
    expectedLineVersion: number;
    expectedOrderVersion: number;
    managerPin: string;
    reason: string;
  },
  operationId = createIdempotencyKey()
): Promise<OrderCart> {
  return request<OrderCart>(
    `/api/v1/orders/${orderId}/cart/lines/${lineId}/manager-reduction`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify(payload)
    }
  );
}

export function deleteOrderLine(
  orderId: string,
  lineId: string,
  expectedOrderVersion: number,
  operationId = createIdempotencyKey()
): Promise<OrderCart> {
  return request<OrderCart>(
    `/api/v1/orders/${orderId}/cart/lines/${lineId}?expectedOrderVersion=${expectedOrderVersion}`,
    {
      method: "DELETE",
      headers: { "Idempotency-Key": operationId }
    }
  );
}


export function getCurrentShiftSummary(): Promise<ShiftSummary | null> {
  return request<ShiftSummary | null>("/api/v1/cashier/shifts/current");
}

export function closeCurrentShift(
  countedCashMinor: number,
  closingNote: string,
  expectedVersion: number
): Promise<ShiftSummary> {
  return request<ShiftSummary>("/api/v1/cashier/shifts/close", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      countedCashMinor,
      closingNote: closingNote || null,
      expectedVersion
    })
  });
}

export function addCashMovement(
  movementType: CashMovementType,
  amountMinor: number,
  reason: string
): Promise<CashMovement> {
  return request<CashMovement>("/api/v1/cashier/shifts/cash-movements", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ movementType, amountMinor, reason })
  });
}

export async function getFloorPlan(): Promise<DiningFloor[]> {
  const response = await request<{ items: DiningFloor[] }>(
    "/api/v1/cashier/floor-plan"
  );
  return response.items;
}

export async function searchCashierCustomers(search?: string): Promise<Customer[]> {
  const response = await request<{ items: Customer[] }>(
    `/api/v1/cashier/customers${search ? `?search=${encodeURIComponent(search)}` : ""}`
  );
  return response.items;
}

export function saveCashierCustomer(
  customer: Partial<Customer> & { name: string }
): Promise<Customer> {
  const url = customer.id
    ? `/api/v1/cashier/customers/${customer.id}`
    : "/api/v1/cashier/customers";
  return request<Customer>(url, {
    method: customer.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      name: customer.name,
      phone: customer.phone ?? null,
      email: customer.email ?? null,
      notes: customer.notes ?? null,
      isActive: customer.isActive ?? true,
      addresses: (customer.addresses ?? []).map((address) => ({
        id: address.id ?? null,
        label: address.label,
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2 ?? null,
        city: address.city ?? null,
        region: address.region ?? null,
        postalCode: address.postalCode ?? null,
        deliveryInstructions: address.deliveryInstructions ?? null,
        latitude: address.latitude ?? null,
        longitude: address.longitude ?? null,
        isDefault: address.isDefault,
        expectedVersion: address.version ?? null
      })),
      expectedVersion: customer.version ?? null
    })
  });
}

export function getOrderWorkflow(orderId: string): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${orderId}/workflow`
  );
}

export function updateCashierOrderContext(
  workflow: OrderWorkflow,
  input: {
    serviceType: ServiceType;
    customerId: string | null;
    tableId: string | null;
    guestCount: number | null;
    contactName: string | null;
    contactPhone: string | null;
    vehicleNumber: string | null;
    deliveryAddressId: string | null;
    deliveryFeeMinor: number;
    deliveryInstructions: string | null;
    externalReference: string | null;
    promisedAt: string | null;
    taxInvoiceRequested: boolean;
    taxInvoiceCompanyName: string | null;
    taxInvoiceTaxRegistrationNumber: string | null;
    taxInvoiceCompanyAddress: string | null;
  },
  operationId = createIdempotencyKey()
): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${workflow.order.id}/context`,
    {
      method: "PUT",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({
        ...input,
        expectedVersion: workflow.order.version
      })
    }
  );
}

export function correctPaidCashierOrderCustomerData(
  workflow: OrderWorkflow,
  input: {
    customerDataSourceId: string | null;
    contactName: string | null;
    contactPhone: string | null;
    taxInvoiceRequested: boolean;
    taxInvoiceCompanyName: string | null;
    taxInvoiceTaxRegistrationNumber: string | null;
    taxInvoiceCompanyAddress: string | null;
    reason: string;
  },
  operationId = createIdempotencyKey()
): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${workflow.order.id}/paid-customer-data`,
    {
      method: "PUT",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({
        ...input,
        expectedOrderVersion: workflow.order.version
      })
    }
  );
}

export function holdCashierOrder(
  workflow: OrderWorkflow,
  reason: string,
  operationId = createIdempotencyKey()
): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${workflow.order.id}/hold`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({
        reason,
        expectedVersion: workflow.order.version
      })
    }
  );
}

export function resumeCashierOrder(
  workflow: OrderWorkflow,
  operationId = createIdempotencyKey()
): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${workflow.order.id}/resume`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({ expectedVersion: workflow.order.version })
    }
  );
}

export function cancelCashierOrder(
  workflow: OrderWorkflow,
  reason: string
): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${workflow.order.id}/cancel`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({
        reason,
        expectedVersion: workflow.order.version
      })
    }
  );
}

export function reopenCashierClosedOrder(
  workflow: OrderWorkflow,
  reason: string,
  operationId = createIdempotencyKey()
): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${workflow.order.id}/reopen`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({
        reason,
        expectedVersion: workflow.order.version
      })
    }
  );
}

export function sendCashierOrder(workflow: OrderWorkflow): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${workflow.order.id}/send`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({ expectedVersion: workflow.order.version })
    }
  );
}

export function markCashierOrderReady(
  workflow: OrderWorkflow
): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${workflow.order.id}/ready`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({ expectedVersion: workflow.order.version })
    }
  );
}

export function moveCashierOrderToTable(
  workflow: OrderWorkflow,
  tableId: string,
  guestCount: number,
  expectedTableVersion: number
): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${workflow.order.id}/move-table`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({
        tableId,
        guestCount,
        expectedOrderVersion: workflow.order.version,
        expectedTableVersion
      })
    }
  );
}

export function mergeCashierOrders(
  source: OrderWorkflow,
  target: OrderWorkflow
): Promise<OrderWorkflow> {
  return request<OrderWorkflow>("/api/v1/cashier/orders/merge", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      sourceOrderId: source.order.id,
      targetOrderId: target.order.id,
      expectedSourceVersion: source.order.version,
      expectedTargetVersion: target.order.version
    })
  });
}

export function splitCashierOrder(
  source: OrderWorkflow,
  lineIds: string[],
  newOrderServiceType: ServiceType,
  newTableId: string | null,
  guestCount: number | null
): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${source.order.id}/split`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({
        lineIds,
        newOrderServiceType,
        newTableId,
        guestCount,
        expectedSourceVersion: source.order.version
      })
    }
  );
}

export function applyCashierOrderDiscount(
  workflow: OrderWorkflow,
  discountType: DiscountType,
  basisPoints: number | null,
  amountMinor: number | null,
  reason: string,
  approvedByUserId: string | null
): Promise<OrderWorkflow> {
  return request<OrderWorkflow>(
    `/api/v1/cashier/orders/${workflow.order.id}/discount`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({
        discountType,
        basisPoints,
        amountMinor,
        reason,
        approvedByUserId,
        expectedOrderVersion: workflow.order.version
      })
    }
  );
}

export function generateCashierPreBill(
  orderId: string,
  expectedOrderVersion: number,
  operationId = createIdempotencyKey()
): Promise<PreBill> {
  return request<PreBill>(
    `/api/v1/cashier/orders/${orderId}/pre-bill`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({ expectedOrderVersion })
    }
  );
}

export function captureCashierPayment(
  workflow: OrderWorkflow,
  method: PaymentMethod,
  amountMinor: number,
  tenderedMinor: number,
  provider: string | null,
  providerReference: string | null,
  clientOperationId = createIdempotencyKey(),
  financialCustomerId: string | null = null,
  giftCardCode?: string,
  giftCardCurrency?: string
): Promise<PaymentResult> {
  return request<PaymentResult>(
    `/api/v1/cashier/orders/${workflow.order.id}/payments`,
    {
      method: "POST",
      headers: { "Idempotency-Key": clientOperationId },
      body: JSON.stringify({
        giftCardCode,
        giftCardCurrency,
        method,
        amountMinor,
        tenderedMinor,
        provider,
        providerReference,
        clientOperationId,
        financialCustomerId,
      expectedOrderVersion: workflow.order.version
      })
    }
  );
}


export async function searchCashierTransactions(
  search?: string,
  from?: string,
  to?: string
): Promise<TransactionSummary[]> {
  const query = new URLSearchParams();
  if (search) query.set("search", search);
  if (from) query.set("from", from);
  if (to) query.set("to", to);
  const response = await request<{ items: TransactionSummary[] }>(
    `/api/v1/cashier/orders/transactions?${query.toString()}`
  );
  return response.items;
}

export function getCashierReceipt(orderId: string): Promise<Receipt | null> {
  return request<Receipt | null>(
    `/api/v1/cashier/orders/${orderId}/receipt`
  );
}

export async function getCashierRefunds(orderId: string): Promise<Refund[]> {
  const response = await request<{ items: Refund[] }>(
    `/api/v1/cashier/orders/${orderId}/refunds`
  );
  return response.items;
}

export function createCashierRefund(
  workflow: OrderWorkflow,
  reason: string,
  lines: Array<{ originalOrderLineId: string; quantityMilli: number }>,
  approvedByUserId: string | null
): Promise<Refund> {
  return request<Refund>(
    `/api/v1/cashier/orders/${workflow.order.id}/refunds`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({
        reason,
        lines,
        approvedByUserId,
        approvalRequestId: null
      })
    }
  );
}

export type CashierRefundActionResult = {
  executed: boolean;
  approvalRequired: boolean;
  refund: Refund | null;
  approvalRequestId: string | null;
  approvalExpiresAt: string | null;
};

export function requestCashierRefund(
  workflow: OrderWorkflow,
  reason: string,
  lines: Array<{ originalOrderLineId: string; quantityMilli: number }>,
  operationId = createIdempotencyKey(),
  giftCardReplacement?: GiftCardReplacement,
  previewToken?: string
): Promise<CashierRefundActionResult> {
  return request<CashierRefundActionResult>(
    `/api/v1/cashier/orders/${workflow.order.id}/refund-action`,
    {
      method: "POST",
      headers: { "Idempotency-Key": operationId },
      body: JSON.stringify({
        giftCardReplacement,
        previewToken,
        reason,
        lines,
        approvedByUserId: null,
        approvalRequestId: null
      })
    }
  );
}

export function getInventoryOverview(): Promise<InventoryOverview> {
  return request<InventoryOverview>("/api/v1/inventory/overview");
}

export async function getWarehouses(): Promise<Warehouse[]> {
  return (await request<{ items: Warehouse[] }>("/api/v1/inventory/warehouses")).items;
}

export function saveWarehouse(
  warehouse: Partial<Warehouse> & {
    branchId: string;
    code: string;
    nameAr: string;
    nameEn: string;
  }
): Promise<Warehouse> {
  const url = warehouse.id
    ? `/api/v1/inventory/warehouses/${warehouse.id}`
    : "/api/v1/inventory/warehouses";
  return request<Warehouse>(url, {
    method: warehouse.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      branchId: warehouse.branchId,
      code: warehouse.code,
      nameAr: warehouse.nameAr,
      nameEn: warehouse.nameEn,
      isDefault: warehouse.isDefault ?? false,
      isActive: warehouse.isActive ?? true,
      expectedVersion: warehouse.version ?? null
    })
  });
}

export async function getReorderRules(): Promise<ReorderRule[]> {
  return (await request<{ items: ReorderRule[] }>("/api/v1/inventory/reorder-rules")).items;
}

export function saveReorderRule(
  rule: Partial<ReorderRule> & {
    warehouseId: string;
    productVariantId: string;
    minimumQuantityMilli: number;
    reorderQuantityMilli: number;
    maximumQuantityMilli: number;
  }
): Promise<ReorderRule> {
  const url = rule.id
    ? `/api/v1/inventory/reorder-rules/${rule.id}`
    : "/api/v1/inventory/reorder-rules";
  return request<ReorderRule>(url, {
    method: rule.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      warehouseId: rule.warehouseId,
      productVariantId: rule.productVariantId,
      minimumQuantityMilli: rule.minimumQuantityMilli,
      reorderQuantityMilli: rule.reorderQuantityMilli,
      maximumQuantityMilli: rule.maximumQuantityMilli,
      isActive: rule.isActive ?? true,
      expectedVersion: rule.version ?? null
    })
  });
}

export async function getStockBalances(
  warehouseId?: string,
  search?: string,
  lowOnly = false
): Promise<StockBalance[]> {
  const query = new URLSearchParams({ lowOnly: String(lowOnly) });
  if (warehouseId) query.set("warehouseId", warehouseId);
  if (search) query.set("search", search);
  return (await request<{ items: StockBalance[] }>(`/api/v1/inventory/balances?${query}`)).items;
}

export function postInventoryAdjustment(input: {
  warehouseId: string;
  productVariantId: string;
  quantityMilli: number;
  unitCostMinor: number;
  reason: string;
}): Promise<null> {
  return request<null>("/api/v1/inventory/adjustments", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify(input)
  });
}

export function postInventoryTransfer(input: {
  sourceWarehouseId: string;
  destinationWarehouseId: string;
  productVariantId: string;
  quantityMilli: number;
}): Promise<null> {
  return request<null>("/api/v1/inventory/transfers", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify(input)
  });
}

export function postInventoryCount(input: {
  warehouseId: string;
  productVariantId: string;
  quantityMilli: number;
  unitCostMinor: number;
  reason: string;
}): Promise<null> {
  return request<null>("/api/v1/inventory/counts", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify(input)
  });
}

export async function getSuppliers(search?: string): Promise<Supplier[]> {
  const query = search ? `?search=${encodeURIComponent(search)}` : "";
  return (await request<{ items: Supplier[] }>(`/api/v1/purchasing/suppliers${query}`)).items;
}

export function saveSupplier(
  supplier: Partial<Supplier> & { legalName: string; currency: string }
): Promise<Supplier> {
  const url = supplier.id
    ? `/api/v1/purchasing/suppliers/${supplier.id}`
    : "/api/v1/purchasing/suppliers";
  return request<Supplier>(url, {
    method: supplier.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      legalName: supplier.legalName,
      tradeName: supplier.tradeName ?? null,
      phone: supplier.phone ?? null,
      email: supplier.email ?? null,
      currency: supplier.currency,
      paymentTermsDays: supplier.paymentTermsDays ?? 0,
      status: supplier.status ?? "Active",
      expectedVersion: supplier.version ?? null
    })
  });
}

export async function getPurchaseOrders(): Promise<PurchaseOrder[]> {
  return (await request<{ items: PurchaseOrder[] }>("/api/v1/purchasing/purchase-orders")).items;
}

export function getPurchaseOrder(id: string): Promise<PurchaseOrderDetail> {
  return request<PurchaseOrderDetail>(`/api/v1/purchasing/purchase-orders/${id}`);
}

export function savePurchaseOrder(input: {
  id?: string;
  branchId: string;
  warehouseId: string;
  supplierId: string;
  currency: string;
  expectedAt: string | null;
  lines: PurchaseOrderLineInput[];
  version?: number;
}): Promise<PurchaseOrder> {
  const url = input.id
    ? `/api/v1/purchasing/purchase-orders/${input.id}`
    : "/api/v1/purchasing/purchase-orders";
  return request<PurchaseOrder>(url, {
    method: input.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      branchId: input.branchId,
      warehouseId: input.warehouseId,
      supplierId: input.supplierId,
      currency: input.currency,
      expectedAt: input.expectedAt,
      lines: input.lines,
      expectedVersion: input.version ?? null
    })
  });
}

export function approvePurchaseOrder(id: string, expectedVersion: number): Promise<PurchaseOrder> {
  return request<PurchaseOrder>(
    `/api/v1/purchasing/purchase-orders/${id}/approve?expectedVersion=${expectedVersion}`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() }
    }
  );
}

export async function getGoodsReceipts(): Promise<GoodsReceipt[]> {
  return (await request<{ items: GoodsReceipt[] }>("/api/v1/purchasing/goods-receipts")).items;
}

export function receiveGoods(input: {
  warehouseId: string;
  supplierId: string;
  purchaseOrderId: string | null;
  supplierInvoiceNumber: string;
  lines: PurchaseOrderLineInput[];
}): Promise<GoodsReceipt> {
  return request<GoodsReceipt>("/api/v1/purchasing/goods-receipts", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify(input)
  });
}

export function getRestaurantOverview(): Promise<RestaurantOverview> {
  return request<RestaurantOverview>("/api/v1/restaurant/overview");
}
export async function getRecipes(search?: string, status?: RecipeStatus): Promise<Recipe[]> {
  const query = new URLSearchParams();
  if (search) query.set("search", search);
  if (status) query.set("status", status);
  return (await request<{ items: Recipe[] }>(`/api/v1/restaurant/recipes?${query}`)).items;
}
export function saveRecipe(recipe: Partial<Recipe> & { productVariantId: string; code: string; nameAr: string; nameEn: string; status: RecipeStatus; yieldQuantityMilli: number; preparationMinutes: number; components: Recipe["components"] }): Promise<Recipe> {
  const url = recipe.id ? `/api/v1/restaurant/recipes/${recipe.id}` : "/api/v1/restaurant/recipes";
  return request<Recipe>(url, { method: recipe.id ? "PUT" : "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({
    productVariantId: recipe.productVariantId, code: recipe.code, nameAr: recipe.nameAr, nameEn: recipe.nameEn,
    status: recipe.status, yieldQuantityMilli: recipe.yieldQuantityMilli, preparationMinutes: recipe.preparationMinutes,
    instructions: recipe.instructions ?? null,
    components: recipe.components.map(component => ({ id: component.id ?? null, ingredientVariantId: component.ingredientVariantId, quantityMilli: component.quantityMilli, wasteBasisPoints: component.wasteBasisPoints, isOptional: component.isOptional, sortOrder: component.sortOrder, expectedVersion: component.version ?? null })),
    expectedVersion: recipe.version ?? null
  }) });
}
export async function getKitchenStations(): Promise<KitchenStation[]> {
  return (await request<{ items: KitchenStation[] }>("/api/v1/restaurant/stations")).items;
}
export function saveKitchenStation(station: Partial<KitchenStation> & { code: string; nameAr: string; nameEn: string; stationType: KitchenStation["stationType"] }): Promise<KitchenStation> {
  const url = station.id ? `/api/v1/restaurant/stations/${station.id}` : "/api/v1/restaurant/stations";
  return request<KitchenStation>(url, { method: station.id ? "PUT" : "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({
    code: station.code, nameAr: station.nameAr, nameEn: station.nameEn, stationType: station.stationType,
    warningAfterMinutes: station.warningAfterMinutes ?? 10, criticalAfterMinutes: station.criticalAfterMinutes ?? 20,
    deviceCode: station.deviceCode ?? null, printerCode: station.printerCode ?? null, isExpo: station.isExpo ?? false,
    isDefault: station.isDefault ?? false, isActive: station.isActive ?? true, sortOrder: station.sortOrder ?? 0,
    expectedVersion: station.version ?? null
  }) });
}
export async function getKitchenRoutingRules(): Promise<KitchenRoutingRule[]> {
  return (await request<{ items: KitchenRoutingRule[] }>("/api/v1/restaurant/routing-rules")).items;
}
export function saveKitchenRoutingRule(rule: Partial<KitchenRoutingRule> & { stationId: string; priority: number }): Promise<KitchenRoutingRule> {
  const url = rule.id ? `/api/v1/restaurant/routing-rules/${rule.id}` : "/api/v1/restaurant/routing-rules";
  return request<KitchenRoutingRule>(url, { method: rule.id ? "PUT" : "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({
    stationId: rule.stationId, productVariantId: rule.productVariantId ?? null, productId: rule.productId ?? null,
    categoryId: rule.categoryId ?? null, priority: rule.priority, isActive: rule.isActive ?? true,
    expectedVersion: rule.version ?? null
  }) });
}
export function getKitchenBoard(stationId?: string): Promise<KitchenBoard> {
  return request<KitchenBoard>(`/api/v1/restaurant/kds${stationId ? `?stationId=${stationId}` : ""}`);
}
export function transitionKitchenTicket(ticket: KitchenTicket, action: "accept" | "start" | "ready" | "complete" | "recall", note?: string): Promise<KitchenTicket> {
  return request<KitchenTicket>(`/api/v1/restaurant/kds/tickets/${ticket.id}/${action}`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedVersion: ticket.version, note: note ?? null }) });
}
export function setKitchenTicketPriority(ticket: KitchenTicket, priority: KitchenPriority): Promise<KitchenTicket> {
  return request<KitchenTicket>(`/api/v1/restaurant/kds/tickets/${ticket.id}/priority/${priority}`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedVersion: ticket.version, note: null }) });
}
export function transitionKitchenItem(ticket: KitchenTicket, itemId: string, version: number, action: "start" | "ready"): Promise<KitchenTicket> {
  return request<KitchenTicket>(`/api/v1/restaurant/kds/tickets/${ticket.id}/items/${itemId}/${action}`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedVersion: version, note: null }) });
}
export async function getReservations(from: string, to: string, status?: ReservationStatus): Promise<Reservation[]> {
  const q = new URLSearchParams({ from, to }); if (status) q.set("status", status);
  return (await request<{ items: Reservation[] }>(`/api/v1/restaurant/reservations?${q}`)).items;
}
export function saveReservation(reservation: Partial<Reservation> & { customerName: string; phone: string; guestCount: number; reservationAt: string; durationMinutes: number }): Promise<Reservation> {
  const url = reservation.id ? `/api/v1/restaurant/reservations/${reservation.id}` : "/api/v1/restaurant/reservations";
  return request<Reservation>(url, { method: reservation.id ? "PUT" : "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({
    customerId: reservation.customerId ?? null, customerName: reservation.customerName, phone: reservation.phone,
    guestCount: reservation.guestCount, reservationAt: reservation.reservationAt, durationMinutes: reservation.durationMinutes,
    tableId: reservation.tableId ?? null, source: reservation.source ?? null, specialRequests: reservation.specialRequests ?? null,
    expectedVersion: reservation.version ?? null
  }) });
}
export function transitionReservation(reservation: Reservation, action: "confirm" | "arrive" | "seat" | "complete" | "no-show" | "cancel", tableId?: string | null, note?: string): Promise<Reservation> {
  return request<Reservation>(`/api/v1/restaurant/reservations/${reservation.id}/${action}`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedVersion: reservation.version, tableId: tableId ?? null, note: note ?? null }) });
}
export async function getWaitlist(status?: WaitlistStatus): Promise<WaitlistEntry[]> {
  return (await request<{ items: WaitlistEntry[] }>(`/api/v1/restaurant/waitlist${status ? `?status=${status}` : ""}`)).items;
}
export function addWaitlistEntry(input: { customerName: string; phone: string; guestCount: number; quotedWaitMinutes: number; notes: string | null }): Promise<WaitlistEntry> {
  return request<WaitlistEntry>("/api/v1/restaurant/waitlist", { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify(input) });
}
export function transitionWaitlistEntry(entry: WaitlistEntry, action: "notify" | "seat" | "cancel" | "left", tableId?: string | null, note?: string): Promise<WaitlistEntry> {
  return request<WaitlistEntry>(`/api/v1/restaurant/waitlist/${entry.id}/${action}`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedVersion: entry.version, tableId: tableId ?? null, note: note ?? null }) });
}
export async function getDeliveryDrivers(): Promise<DeliveryDriver[]> {
  return (await request<{ items: DeliveryDriver[] }>("/api/v1/restaurant/drivers")).items;
}
export function saveDeliveryDriver(driver: Partial<DeliveryDriver> & { name: string; phone: string; status: DriverStatus }): Promise<DeliveryDriver> {
  const url = driver.id ? `/api/v1/restaurant/drivers/${driver.id}` : "/api/v1/restaurant/drivers";
  return request<DeliveryDriver>(url, { method: driver.id ? "PUT" : "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ userId: driver.userId ?? null, name: driver.name, phone: driver.phone, vehicleNumber: driver.vehicleNumber ?? null, status: driver.status, isActive: driver.isActive ?? true, expectedVersion: driver.version ?? null }) });
}
export async function getDeliveryBoard(): Promise<DeliveryOrder[]> {
  return (await request<{ items: DeliveryOrder[] }>("/api/v1/restaurant/delivery")).items;
}
export function assignDeliveryOrder(order: DeliveryOrder, driverId: string, estimatedMinutes: number | null, distanceMeters: number | null, notes: string | null): Promise<DeliveryOrder> {
  return request<DeliveryOrder>(`/api/v1/restaurant/delivery/${order.orderId}/assign`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ driverId, estimatedMinutes, distanceMeters, notes, expectedFulfillmentVersion: order.fulfillmentVersion }) });
}
export function transitionDeliveryOrder(order: DeliveryOrder, action: "pickup" | "out-for-delivery" | "deliver" | "fail" | "cancel", note?: string): Promise<DeliveryOrder> {
  return request<DeliveryOrder>(`/api/v1/restaurant/delivery/${order.orderId}/${action}`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedAssignmentVersion: order.assignmentVersion, note: note ?? null }) });
}
export async function getHandovers(status?: HandoverStatus): Promise<Handover[]> {
  return (await request<{ items: Handover[] }>(`/api/v1/restaurant/handovers${status ? `?status=${status}` : ""}`)).items;
}
export function transitionHandover(handover: Handover, action: "quality-check" | "hand-over" | "complete" | "cancel", recipientType: HandoverRecipientType, recipientName?: string | null, note?: string): Promise<Handover> {
  return request<Handover>(`/api/v1/restaurant/handovers/${handover.id}/${action}`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedVersion: handover.version, recipientType, recipientName: recipientName ?? null, note: note ?? null }) });
}


export function getCrmOverview(): Promise<CrmOverview> { return request<CrmOverview>("/api/v1/crm/overview"); }
export async function searchCrmCustomers(search?:string,status?:CustomerStatus,segmentId?:string,loyaltyTierId?:string,duplicateCandidatesOnly=false,limit?:number):Promise<CustomerListItem[]>{const q=new URLSearchParams();if(search)q.set("search",search);if(status)q.set("status",status);if(segmentId)q.set("segmentId",segmentId);if(loyaltyTierId)q.set("loyaltyTierId",loyaltyTierId);q.set("duplicateCandidatesOnly",String(duplicateCandidatesOnly));if(limit!==undefined)q.set("limit",String(limit));return(await request<{items:CustomerListItem[]}>(`/api/v1/crm/customers?${q}`)).items;}
export function getCustomer360(id:string):Promise<CustomerProfile>{return request<CustomerProfile>(`/api/v1/crm/customers/${id}`);}
export function getCustomerReceivables(id:string):Promise<CustomerReceivableSummary>{return request<CustomerReceivableSummary>(`/api/v1/crm/customers/${id}/receivables`);}
export function settleCustomerReceivable(id:string,input:{amountMinor:number;method:PaymentMethod;tenderedMinor:number;provider:string|null;providerReference:string|null}):Promise<CustomerAccountSettlementResponse>{return request<CustomerAccountSettlementResponse>(`/api/v1/crm/customers/${id}/receivables/settlements`,{method:"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({...input,clientOperationId:createIdempotencyKey()})});}
export function saveCrmCustomer(customer:Partial<CustomerProfile>&{name:string;customerType:CustomerProfile["customerType"];status:CustomerProfile["status"];preferredLanguage:string;addresses:CustomerProfile["addresses"];tags:CustomerTag[]}):Promise<CustomerProfile>{const url=customer.id?`/api/v1/crm/customers/${customer.id}`:"/api/v1/crm/customers";return request<CustomerProfile>(url,{method:customer.id?"PUT":"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({name:customer.name,customerType:customer.customerType,status:customer.status,companyName:customer.companyName??null,taxRegistrationNumber:customer.taxRegistrationNumber??null,birthDate:customer.birthDate??null,preferredLanguage:customer.preferredLanguage,phone:customer.phone??null,email:customer.email??null,notes:customer.profileNotes??null,blacklistReason:customer.blacklistReason??null,addresses:customer.addresses.map(a=>({id:a.id??null,label:a.label,addressLine1:a.addressLine1,addressLine2:a.addressLine2??null,city:a.city??null,region:a.region??null,postalCode:a.postalCode??null,deliveryInstructions:a.deliveryInstructions??null,latitude:a.latitude??null,longitude:a.longitude??null,isDefault:a.isDefault,expectedVersion:a.version??null})),tagIds:customer.tags.map(x=>x.id),expectedVersion:customer.version??null})});}
export function addCrmCustomerNote(customerId:string,input:{noteType:CustomerNote["noteType"];text:string;isPinned:boolean}):Promise<CustomerNote>{return request<CustomerNote>(`/api/v1/crm/customers/${customerId}/notes`,{method:"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify(input)});}
export function saveCrmCustomerConsent(customerId:string,consent:Partial<CustomerConsent>&{channel:CustomerConsent["channel"];status:CustomerConsent["status"];purpose:string;effectiveAt:string}):Promise<CustomerConsent>{const url=consent.id?`/api/v1/crm/customers/${customerId}/consents/${consent.id}`:`/api/v1/crm/customers/${customerId}/consents`;return request<CustomerConsent>(url,{method:consent.id?"PUT":"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({...consent,expectedVersion:consent.version??null})});}
export function mergeCrmCustomers(input:{sourceCustomerId:string;targetCustomerId:string;reason:string;sourceExpectedVersion:number;targetExpectedVersion:number}){return request<{mergeId:string;sourceCustomerId:string;targetCustomerId:string;mergedAt:string}>("/api/v1/crm/customers/merge",{method:"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify(input)});}
export async function getCrmTags():Promise<CustomerTag[]>{return(await request<{items:CustomerTag[]}>("/api/v1/crm/tags")).items;}
export function saveCrmTag(tag:Partial<CustomerTag>&{code:string;nameAr:string;nameEn:string}):Promise<CustomerTag>{const url=tag.id?`/api/v1/crm/tags/${tag.id}`:"/api/v1/crm/tags";return request<CustomerTag>(url,{method:tag.id?"PUT":"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({id:tag.id??"00000000-0000-0000-0000-000000000000",code:tag.code,nameAr:tag.nameAr,nameEn:tag.nameEn,colorToken:tag.colorToken??null,isSystem:tag.isSystem??false,isActive:tag.isActive??true,version:tag.version??0})});}
export async function getCrmSegments():Promise<CustomerSegment[]>{return(await request<{items:CustomerSegment[]}>("/api/v1/crm/segments")).items;}
export function saveCrmSegment(segment:Partial<CustomerSegment>&{code:string;nameAr:string;nameEn:string;rule:SegmentRule}):Promise<CustomerSegment>{const url=segment.id?`/api/v1/crm/segments/${segment.id}`:"/api/v1/crm/segments";return request<CustomerSegment>(url,{method:segment.id?"PUT":"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({code:segment.code,nameAr:segment.nameAr,nameEn:segment.nameEn,description:segment.description??null,rule:segment.rule,isDynamic:segment.isDynamic??true,isActive:segment.isActive??true,expectedVersion:segment.version??null})});}
export function refreshCrmSegment(segment:CustomerSegment):Promise<CustomerSegment>{return request<CustomerSegment>(`/api/v1/crm/segments/${segment.id}/refresh`,{method:"POST",headers:{"Idempotency-Key":createIdempotencyKey()}});}
export async function getLoyaltyPrograms():Promise<LoyaltyProgram[]>{return(await request<{items:LoyaltyProgram[]}>("/api/v1/crm/loyalty/programs")).items;}
export function saveLoyaltyProgram(program:Partial<LoyaltyProgram>&{code:string;nameAr:string;nameEn:string;status:LoyaltyProgram["status"];currency:string}):Promise<LoyaltyProgram>{const url=program.id?`/api/v1/crm/loyalty/programs/${program.id}`:"/api/v1/crm/loyalty/programs";return request<LoyaltyProgram>(url,{method:program.id?"PUT":"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({code:program.code,nameAr:program.nameAr,nameEn:program.nameEn,status:program.status,currency:program.currency,earnPointsPerCurrencyUnit:program.earnPointsPerCurrencyUnit??1,redemptionValueMinorPerPoint:program.redemptionValueMinorPerPoint??1,minimumRedeemPoints:program.minimumRedeemPoints??100,pointsExpiryDays:program.pointsExpiryDays??365,earnOnNetBeforeTax:program.earnOnNetBeforeTax??true,excludeDiscountedValue:program.excludeDiscountedValue??false,expectedVersion:program.version??null})});}
export async function getLoyaltyTiers(programId?:string):Promise<LoyaltyTier[]>{return(await request<{items:LoyaltyTier[]}>(`/api/v1/crm/loyalty/tiers${programId?`?programId=${programId}`:""}`)).items;}
export function saveLoyaltyTier(tier:Partial<LoyaltyTier>&{programId:string;code:string;nameAr:string;nameEn:string}):Promise<LoyaltyTier>{const url=tier.id?`/api/v1/crm/loyalty/tiers/${tier.id}`:"/api/v1/crm/loyalty/tiers";return request<LoyaltyTier>(url,{method:tier.id?"PUT":"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({programId:tier.programId,code:tier.code,nameAr:tier.nameAr,nameEn:tier.nameEn,spendThresholdMinor:tier.spendThresholdMinor??0,earnMultiplierBasisPoints:tier.earnMultiplierBasisPoints??10000,benefitsJson:tier.benefitsJson??null,colorToken:tier.colorToken??null,sortOrder:tier.sortOrder??0,isActive:tier.isActive??true,expectedVersion:tier.version??null})});}
export function getCustomerLoyalty(id:string):Promise<LoyaltyAccountSummary>{return request<LoyaltyAccountSummary>(`/api/v1/crm/customers/${id}/loyalty`);}
export async function getCustomerLoyaltyTransactions(id:string):Promise<LoyaltyTransaction[]>{return(await request<{items:LoyaltyTransaction[]}>(`/api/v1/crm/customers/${id}/loyalty/transactions`)).items;}
export function adjustCustomerLoyalty(id:string,pointsDelta:number,reason:string):Promise<LoyaltyAccountSummary>{return request<LoyaltyAccountSummary>(`/api/v1/crm/customers/${id}/loyalty/adjust`,{method:"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({pointsDelta,reason,clientOperationId:createIdempotencyKey()})});}
export function redeemLoyaltyToWallet(id:string,points:number,currency:string):Promise<WalletAccountSummary>{return request<WalletAccountSummary>(`/api/v1/crm/customers/${id}/loyalty/redeem-to-wallet`,{method:"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({points,currency,reason:"Loyalty redemption",clientOperationId:createIdempotencyKey()})});}
export function getCustomerWallet(id:string,currency?:string):Promise<WalletAccountSummary>{return request<WalletAccountSummary>(`/api/v1/crm/customers/${id}/wallet${currency ? `?currency=${encodeURIComponent(currency)}` : ""}`);}
export async function getCustomerWalletTransactions(id:string):Promise<WalletTransaction[]>{return(await request<{items:WalletTransaction[]}>(`/api/v1/crm/customers/${id}/wallet/transactions`)).items;}
export function adjustCustomerWallet(id:string,amountDeltaMinor:number,reason:string,currency:string):Promise<WalletAccountSummary>{return request<WalletAccountSummary>(`/api/v1/crm/customers/${id}/wallet/adjust`,{method:"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({amountDeltaMinor,currency,reason,clientOperationId:createIdempotencyKey()})});}
export async function getGiftCards(status?:GiftCardStatus):Promise<GiftCard[]>{return(await request<{items:GiftCard[]}>(`/api/v1/crm/gift-cards${status?`?status=${status}`:""}`)).items;}
export function issueGiftCard(input:{valueMinor:number;currency:string;issuedToCustomerId:string|null;recipientName:string|null;recipientContact:string|null;message:string|null;expiresAt:string|null;fundingType:"Paid"|"MerchantGrant";fundingMethod?:"Cash"|"Card"|null;fundingReference?:string|null;fundingReason?:string|null},operationId=createIdempotencyKey()):Promise<GiftCard>{return request<GiftCard>("/api/v1/crm/gift-cards",{method:"POST",headers:{"Idempotency-Key":operationId},body:JSON.stringify(input)});}
export function getGiftCardCode(cardId: string, purpose: "view" | "print"): Promise<GiftCard> { return request<GiftCard>(`/api/v1/crm/gift-cards/${cardId}/code/${purpose}`, { method: "POST", cache: "no-store" }); }
export function redeemGiftCard(input:{code:string;customerId:string;amountMinor:number;currency?:string},operationId=createIdempotencyKey()):Promise<GiftCard>{return request<GiftCard>("/api/v1/crm/gift-cards/redeem",{method:"POST",headers:{"Idempotency-Key":operationId},body:JSON.stringify({...input,clientOperationId:operationId})});}
export async function getCoupons(status?:CouponStatus):Promise<Coupon[]>{return(await request<{items:Coupon[]}>(`/api/v1/crm/coupons${status?`?status=${status}`:""}`)).items;}
export function saveCoupon(coupon:Partial<Coupon>&{code:string;nameAr:string;nameEn:string;couponType:Coupon["couponType"];status:Coupon["status"];startsAt:string}):Promise<Coupon>{const url=coupon.id?`/api/v1/crm/coupons/${coupon.id}`:"/api/v1/crm/coupons";return request<Coupon>(url,{method:coupon.id?"PUT":"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({...coupon,expectedVersion:coupon.version??null})});}
export async function getCampaigns(status?:CampaignStatus):Promise<Campaign[]>{return(await request<{items:Campaign[]}>(`/api/v1/crm/campaigns${status?`?status=${status}`:""}`)).items;}
export function saveCampaign(campaign:Partial<Campaign>&{code:string;nameAr:string;nameEn:string;channel:Campaign["channel"];status:Campaign["status"];subject:string;messageTemplate:string}):Promise<Campaign>{const url=campaign.id?`/api/v1/crm/campaigns/${campaign.id}`:"/api/v1/crm/campaigns";return request<Campaign>(url,{method:campaign.id?"PUT":"POST",headers:{"Idempotency-Key":createIdempotencyKey()},body:JSON.stringify({...campaign,expectedVersion:campaign.version??null})});}
export function launchCampaign(campaign:Campaign){return request<{campaign:Campaign;eligibleRecipients:number;suppressedRecipients:number}>(`/api/v1/crm/campaigns/${campaign.id}/launch?expectedVersion=${campaign.version}`,{method:"POST",headers:{"Idempotency-Key":createIdempotencyKey()}});}
export function applyCashierCoupon(orderId:string,code:string,expectedOrderVersion:number,customerId?:string,idempotencyKey=createIdempotencyKey()):Promise<OrderWorkflow>{return request<OrderWorkflow>(`/api/v1/cashier/orders/${orderId}/coupon`,{method:"POST",headers:{"Idempotency-Key":idempotencyKey},body:JSON.stringify({code,expectedOrderVersion,...(customerId ? {customerId} : {})})});}

export function getExecutiveDashboard(
  scope: ReportScope
): Promise<ExecutiveDashboard> {
  return request<ExecutiveDashboard>("/api/v1/reports/executive", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getSalesAnalytics(
  scope: ReportScope
): Promise<SalesAnalytics> {
  return request<SalesAnalytics>("/api/v1/reports/sales", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getProfitabilityAnalytics(
  scope: ReportScope
): Promise<ProfitabilityAnalytics> {
  return request<ProfitabilityAnalytics>("/api/v1/reports/profitability", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getProductAnalytics(
  scope: ReportScope
): Promise<ProductAnalytics> {
  return request<ProductAnalytics>("/api/v1/reports/products", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getInventoryAnalytics(
  scope: ReportScope
): Promise<InventoryAnalytics> {
  return request<InventoryAnalytics>("/api/v1/reports/inventory", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getPurchasingAnalytics(
  scope: ReportScope
): Promise<PurchasingAnalytics> {
  return request<PurchasingAnalytics>("/api/v1/reports/purchasing", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getCustomerAnalytics(
  scope: ReportScope
): Promise<CustomerAnalytics> {
  return request<CustomerAnalytics>("/api/v1/reports/customers", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getEmployeeAnalytics(
  scope: ReportScope
): Promise<EmployeeAnalytics> {
  return request<EmployeeAnalytics>("/api/v1/reports/employees", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getRestaurantAnalytics(
  scope: ReportScope
): Promise<RestaurantAnalytics> {
  return request<RestaurantAnalytics>("/api/v1/reports/restaurant", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getTaxVatReport(
  scope: ReportScope
): Promise<TaxVatReport> {
  return request<TaxVatReport>("/api/v1/reports/vat", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getCashPaymentsAnalytics(
  scope: ReportScope
): Promise<CashPaymentsAnalytics> {
  return request<CashPaymentsAnalytics>("/api/v1/reports/cash-payments", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getBranchComparison(
  scope: ReportScope
): Promise<BranchComparison> {
  return request<BranchComparison>("/api/v1/reports/branches", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getForecast(
  scope: ReportScope
): Promise<Forecast> {
  return request<Forecast>("/api/v1/reports/forecast", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export async function getFinanceAccounts(currency?: string): Promise<FinanceAccount[]> {
  return (await request<{ items: FinanceAccount[] }>(
    `/api/v1/finance/accounts${currency !== undefined ? `?${new URLSearchParams({ currency })}` : ""}`
  )).items;
}

export async function getFinancialPeriods(): Promise<FinancialPeriod[]> {
  return (await request<{ items: FinancialPeriod[] }>(
    "/api/v1/finance/periods"
  )).items;
}

export async function getJournalEntries(input?: {
  currency?: string;
  from?: string;
  to?: string;
  status?: string;
  search?: string;
}): Promise<JournalEntry[]> {
  const query = new URLSearchParams();
  if (input?.from) query.set("from", input.from);
  if (input?.to) query.set("to", input.to);
  if (input?.status) query.set("status", input.status);
  if (input?.search) query.set("search", input.search);
  if (input?.currency !== undefined) query.set("currency", input.currency);
  return (await request<{ items: JournalEntry[] }>(
    `/api/v1/finance/journals?${query.toString()}`
  )).items;
}

export function createManualJournal(input: {
  postingDate: string;
  currency: string;
  description: string;
  lines: Array<{
    accountId: string;
    debitMinor: number;
    creditMinor: number;
    description: string | null;
    counterpartyType: string | null;
    counterpartyId: string | null;
    costCenterCode: string | null;
  }>;
}): Promise<JournalEntry> {
  return request<JournalEntry>("/api/v1/finance/journals", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify(input)
  });
}

export function postJournal(entry: JournalEntry): Promise<JournalEntry> {
  return request<JournalEntry>(
    `/api/v1/finance/journals/${entry.id}/post?expectedVersion=${entry.version}`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() }
    }
  );
}

export function reverseJournal(
  entry: JournalEntry,
  postingDate: string,
  reason: string
): Promise<JournalEntry> {
  const query = new URLSearchParams({
    postingDate,
    expectedVersion: String(entry.version),
    reason
  });
  return request<JournalEntry>(
    `/api/v1/finance/journals/${entry.id}/reverse?${query.toString()}`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() }
    }
  );
}

export async function getExpenseCategories(): Promise<ExpenseCategory[]> {
  return (await request<{ items: ExpenseCategory[] }>(
    "/api/v1/finance/expense-categories"
  )).items;
}

export function saveExpenseCategory(input: {
  id?: string;
  expenseAccountId: string;
  code: string;
  nameAr: string;
  nameEn: string;
  isActive: boolean;
  version?: number;
}): Promise<ExpenseCategory> {
  const url = input.id
    ? `/api/v1/finance/expense-categories/${input.id}`
    : "/api/v1/finance/expense-categories";
  return request<ExpenseCategory>(url, {
    method: input.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      expenseAccountId: input.expenseAccountId,
      code: input.code,
      nameAr: input.nameAr,
      nameEn: input.nameEn,
      isActive: input.isActive,
      expectedVersion: input.version ?? null
    })
  });
}

export async function getOperatingExpenses(input?: {
  currency?: string;
  from?: string;
  to?: string;
  status?: string;
}): Promise<OperatingExpense[]> {
  const query = new URLSearchParams();
  if (input?.from) query.set("from", input.from);
  if (input?.to) query.set("to", input.to);
  if (input?.status) query.set("status", input.status);
  if (input?.currency !== undefined) query.set("currency", input.currency);
  return (await request<{ items: OperatingExpense[] }>(
    `/api/v1/finance/expenses?${query.toString()}`
  )).items;
}

export function saveOperatingExpense(input: {
  id?: string;
  categoryId: string;
  expenseDate: string;
  description: string;
  vendorName: string | null;
  vendorTaxRegistrationNumber: string | null;
  supplierInvoiceNumber: string | null;
  currency: string;
  netMinor: number;
  taxMinor: number;
  paymentMethod: string;
  paymentReference: string | null;
  version?: number;
}): Promise<OperatingExpense> {
  const url = input.id
    ? `/api/v1/finance/expenses/${input.id}`
    : "/api/v1/finance/expenses";
  return request<OperatingExpense>(url, {
    method: input.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      categoryId: input.categoryId,
      expenseDate: input.expenseDate,
      description: input.description,
      vendorName: input.vendorName,
      vendorTaxRegistrationNumber: input.vendorTaxRegistrationNumber,
      supplierInvoiceNumber: input.supplierInvoiceNumber,
      currency: input.currency,
      netMinor: input.netMinor,
      taxMinor: input.taxMinor,
      paymentMethod: input.paymentMethod,
      paymentReference: input.paymentReference,
      expectedVersion: input.version ?? null
    })
  });
}

export function approveOperatingExpense(
  expense: OperatingExpense
): Promise<OperatingExpense> {
  return request<OperatingExpense>(
    `/api/v1/finance/expenses/${expense.id}/approve?expectedVersion=${expense.version}`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() }
    }
  );
}

export function postOperatingExpense(
  expense: OperatingExpense
): Promise<OperatingExpense> {
  return request<OperatingExpense>(
    `/api/v1/finance/expenses/${expense.id}/post?expectedVersion=${expense.version}`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() }
    }
  );
}

export function getGeneralLedger(
  scope: ReportScope,
  accountId?: string
): Promise<GeneralLedger> {
  return request<GeneralLedger>(
    `/api/v1/finance/general-ledger${accountId ? `?accountId=${accountId}` : ""}`,
    {
      method: "POST",
      body: JSON.stringify(scope)
    }
  );
}

export function getTrialBalance(
  scope: ReportScope
): Promise<TrialBalance> {
  return request<TrialBalance>("/api/v1/finance/trial-balance", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getProfitAndLoss(
  scope: ReportScope
): Promise<ProfitAndLoss> {
  return request<ProfitAndLoss>("/api/v1/finance/profit-loss", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export function getBalanceSheet(
  scope: ReportScope
): Promise<BalanceSheet> {
  return request<BalanceSheet>("/api/v1/finance/balance-sheet", {
    method: "POST",
    body: JSON.stringify(scope)
  });
}

export async function getReportDefinitions(
  dataset?: ReportDataset
): Promise<ReportDefinition[]> {
  return (await request<{ items: ReportDefinition[] }>(
    `/api/v1/reports/definitions${dataset ? `?dataset=${dataset}` : ""}`
  )).items;
}

export async function getReportSchedules(): Promise<ReportSchedule[]> {
  return (await request<{ items: ReportSchedule[] }>(
    "/api/v1/reports/schedules"
  )).items;
}

export function runReport(input: {
  reportDefinitionId: string | null;
  dataset: ReportDataset;
  scope: ReportScope;
  dimensions: string[];
  measures: string[];
  filters: Record<string, unknown>;
  language?: "ar" | "en";
}): Promise<ReportRun> {
  return request<ReportRun>("/api/v1/reports/runs", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify(input)
  });
}

export async function getReportOccurrences(): Promise<ReportOccurrence[]> {
  return (await request<{ items: ReportOccurrence[] }>("/api/v1/reports/occurrences")).items;
}
export function retryReportDelivery(id: string): Promise<ReportDeliveryResult> {
  return request(`/api/v1/reports/deliveries/${id}/retry`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() } });
}

export function runReportSchedule(
  schedule: ReportSchedule
): Promise<ReportRun> {
  return request<ReportRun>(
    `/api/v1/reports/schedules/${schedule.id}/run`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() }
    }
  );
}

export function createReportExport(
  reportRunId: string,
  format: ReportExportFormat
): Promise<ReportExport> {
  return request<ReportExport>("/api/v1/reports/exports", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ reportRunId, format })
  });
}

export async function downloadReportExport(exportId: string, fileName?: string): Promise<void> {
  const generation = captureSessionGeneration();
  const token = getAccessToken();
  const response = await fetch(`/api/v1/reports/exports/${exportId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}, cache: "no-store"
  });
  assertSessionGenerationCurrent(generation);
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.detail ?? payload.title ?? "تعذر تنزيل التقرير / Report download failed.");
  }
  const blob = await response.blob();
  assertSessionGenerationCurrent(generation);
  if (!blob.size) throw new Error("ملف التقرير فارغ / Report file is empty.");
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  const disposition = response.headers.get("content-disposition") ?? "";
  const encoded = disposition.match(new RegExp("filename\\*=UTF-8''([^;]+)", "i"))?.[1];
  const plain = disposition.match(new RegExp("filename=\"?([^\";]+)\"?", "i"))?.[1];
  anchor.download = fileName ?? (encoded ? decodeURIComponent(encoded) : plain) ?? `report-${exportId}`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function rebuildFinancePostings(
  scope: ReportScope
): Promise<{
  salesEntriesCreated: number;
  refundEntriesCreated: number;
  purchaseEntriesCreated: number;
  cashEntriesCreated: number;
  entriesAlreadyPresent: number;
  completedAt: string;
}> {
  return request("/api/v1/finance/rebuild", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify(scope)
  });
}

export function saveFinanceAccount(
  account: Partial<FinanceAccount> & {
    code: string;
    nameAr: string;
    nameEn: string;
    accountType: FinanceAccount["accountType"];
    normalBalance: FinanceAccount["normalBalance"];
    currency: string;
  }
): Promise<FinanceAccount> {
  const url = account.id
    ? `/api/v1/finance/accounts/${account.id}`
    : "/api/v1/finance/accounts";
  return request<FinanceAccount>(url, {
    method: account.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      parentAccountId: account.parentAccountId ?? null,
      code: account.code,
      nameAr: account.nameAr,
      nameEn: account.nameEn,
      accountType: account.accountType,
      normalBalance: account.normalBalance,
      currency: account.currency,
      allowManualPosting: account.allowManualPosting ?? true,
      isActive: account.isActive ?? true,
      expectedVersion: account.version ?? null
    })
  });
}

export function saveFinancialPeriod(input: {
  id?: string;
  periodCode: string;
  startsOn: string;
  endsOn: string;
  version?: number;
}): Promise<FinancialPeriod> {
  const url = input.id
    ? `/api/v1/finance/periods/${input.id}`
    : "/api/v1/finance/periods";
  return request<FinancialPeriod>(url, {
    method: input.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      periodCode: input.periodCode,
      startsOn: input.startsOn,
      endsOn: input.endsOn,
      expectedVersion: input.version ?? null
    })
  });
}

export function changeFinancialPeriodStatus(
  period: FinancialPeriod,
  status: FinancialPeriod["status"],
  note?: string
): Promise<FinancialPeriod> {
  const query = new URLSearchParams({
    expectedVersion: String(period.version),
    note: note ?? ""
  });
  return request<FinancialPeriod>(
    `/api/v1/finance/periods/${period.id}/status/${status}?${query.toString()}`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() }
    }
  );
}

export function saveReportDefinition(
  definition: Partial<ReportDefinition> & {
    code: string;
    nameAr: string;
    nameEn: string;
    dataset: ReportDataset;
    status: ReportDefinition["status"];
    dimensions: string[];
    measures: string[];
  }
): Promise<ReportDefinition> {
  const url = definition.id
    ? `/api/v1/reports/definitions/${definition.id}`
    : "/api/v1/reports/definitions";
  return request<ReportDefinition>(url, {
    method: definition.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      branchId: definition.branchId ?? null,
      code: definition.code,
      nameAr: definition.nameAr,
      nameEn: definition.nameEn,
      description: definition.description ?? null,
      dataset: definition.dataset,
      status: definition.status,
      dimensions: definition.dimensions,
      measures: definition.measures,
      filters: definition.filters ?? {},
      visualization: definition.visualization ?? { type: "table" },
      expectedVersion: definition.version ?? null
    })
  });
}

export function saveReportSchedule(
  schedule: Partial<ReportSchedule> & {
    reportDefinitionId: string;
    name: string;
    frequency: ReportSchedule["frequency"];
    exportFormat: ReportSchedule["exportFormat"];
    timeZone: string;
    recipients: string[];
    nextRunAt: string;
    localStart?: string;
    confirmActivation?: boolean;
  }
): Promise<ReportSchedule> {
  const url = schedule.id
    ? `/api/v1/reports/schedules/${schedule.id}`
    : "/api/v1/reports/schedules";
  return request<ReportSchedule>(url, {
    method: schedule.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      branchId: schedule.branchId ?? null,
      reportDefinitionId: schedule.reportDefinitionId,
      name: schedule.name,
      frequency: schedule.frequency,
      exportFormat: schedule.exportFormat,
      timeZone: schedule.timeZone,
      recipients: schedule.recipients,
      parameters: schedule.parameters ?? {},
      nextRunAt: schedule.nextRunAt,
      isActive: schedule.isActive ?? true,
      expectedVersion: schedule.version ?? null,
      localStart: schedule.localStart ?? null,
      confirmActivation: schedule.confirmActivation ?? false
    })
  });
}


export function getIntegrationsOverview(): Promise<IntegrationOverview> {
  return request<IntegrationOverview>("/api/v1/integrations/overview");
}

export async function getIntegrationConnectors(): Promise<IntegrationConnector[]> {
  return (await request<{ items: IntegrationConnector[] }>(
    "/api/v1/integrations/connectors"
  )).items;
}

export function saveIntegrationConnector(
  connector: Partial<IntegrationConnector> & {
    code: string;
    nameAr: string;
    nameEn: string;
    category: IntegrationConnector["category"];
    providerCode: string;
  }
): Promise<IntegrationConnector> {
  const url = connector.id
    ? `/api/v1/integrations/connectors/${connector.id}`
    : "/api/v1/integrations/connectors";
  return request<IntegrationConnector>(url, {
    method: connector.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      branchId: connector.branchId ?? null,
      code: connector.code,
      nameAr: connector.nameAr,
      nameEn: connector.nameEn,
      category: connector.category,
      providerCode: connector.providerCode,
      baseUrl: connector.baseUrl ?? null,
      config: connector.config ?? {},
      isEnabled: connector.isEnabled ?? false,
      timeoutSeconds: connector.timeoutSeconds ?? 30,
      maxAttempts: connector.maxAttempts ?? 5,
      baseDelaySeconds: connector.baseDelaySeconds ?? 10,
      maxDelaySeconds: connector.maxDelaySeconds ?? 900,
      circuitFailureThreshold: connector.circuitFailureThreshold ?? 5,
      circuitOpenSeconds: connector.circuitOpenSeconds ?? 120,
      expectedVersion: connector.version ?? null
    })
  });
}

export async function getWebhookEndpoints(): Promise<WebhookEndpoint[]> {
  return (await request<{ items: WebhookEndpoint[] }>(
    "/api/v1/integrations/webhooks"
  )).items;
}

export function saveWebhookEndpoint(input: {
  id?: string;
  connectorId: string;
  code: string;
  nameAr: string;
  nameEn: string;
  direction: WebhookEndpoint["direction"];
  eventType: string;
  targetUrl?: string | null;
  authMode: WebhookEndpoint["authMode"];
  signingSecret?: string | null;
  isActive: boolean;
  version?: number;
}): Promise<WebhookEndpoint> {
  const url = input.id
    ? `/api/v1/integrations/webhooks/${input.id}`
    : "/api/v1/integrations/webhooks";
  return request<WebhookEndpoint>(url, {
    method: input.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      connectorId: input.connectorId,
      branchId: null,
      code: input.code,
      nameAr: input.nameAr,
      nameEn: input.nameEn,
      direction: input.direction,
      eventType: input.eventType,
      targetUrl: input.targetUrl ?? null,
      authMode: input.authMode,
      signingSecret: input.signingSecret ?? null,
      isActive: input.isActive,
      expectedVersion: input.version ?? null
    })
  });
}

export async function getIntegrationMessages(): Promise<IntegrationMessage[]> {
  return (await request<{ items: IntegrationMessage[] }>(
    "/api/v1/integrations/messages?limit=100"
  )).items;
}

export function deliverIntegrationMessage(messageId: string): Promise<IntegrationMessage> {
  return request<IntegrationMessage>(
    `/api/v1/integrations/messages/${messageId}/deliver`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() }
    }
  );
}

export async function getIntegrationDeadLetters(): Promise<DeadLetter[]> {
  return (await request<{ items: DeadLetter[] }>(
    "/api/v1/integrations/dead-letters"
  )).items;
}

export function replayIntegrationDeadLetter(item: DeadLetter): Promise<IntegrationMessage> {
  return request<IntegrationMessage>(
    `/api/v1/integrations/dead-letters/${item.id}/replay?expectedVersion=${item.version}`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() }
    }
  );
}

export async function getDataExchangeJobs(): Promise<DataExchangeJob[]> {
  return (await request<{ items: DataExchangeJob[] }>(
    "/api/v1/integrations/data-exchange/jobs"
  )).items;
}

export function runDataExport(
  resourceType: "CONNECTORS" | "FISCAL_DOCUMENTS",
  format: "Csv" | "Json" | "JsonLines"
): Promise<DataExchangeJob> {
  const extension = format === "Csv" ? "csv" : format === "JsonLines" ? "jsonl" : "json";
  return request<DataExchangeJob>("/api/v1/integrations/data-exchange/jobs", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      direction: "Export",
      format,
      resourceType,
      fileName: `${resourceType.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.${extension}`,
      parameters: {},
      contentBase64: null
    })
  });
}

export function getTaxProfile(): Promise<TaxProfile | null> {
  return request<TaxProfile | null>("/api/v1/compliance/uae/tax-profile");
}

export function saveTaxProfile(
  profile: Partial<TaxProfile> & {
    legalNameAr: string;
    legalNameEn: string;
    trn: string;
    addressLine1: string;
    city: string;
    emirate: string;
  }
): Promise<TaxProfile> {
  const url = profile.id
    ? `/api/v1/compliance/uae/tax-profile/${profile.id}`
    : "/api/v1/compliance/uae/tax-profile";
  return request<TaxProfile>(url, {
    method: profile.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      branchId: profile.branchId ?? null,
      legalNameAr: profile.legalNameAr,
      legalNameEn: profile.legalNameEn,
      tradeName: profile.tradeName ?? null,
      trn: profile.trn,
      tradeLicenseNumber: profile.tradeLicenseNumber ?? null,
      legalRegistrationIdentifier: profile.legalRegistrationIdentifier ?? null,
      electronicAddress: profile.electronicAddress ?? null,
      electronicAddressSchemeId: profile.electronicAddressSchemeId ?? "0235",
      addressLine1: profile.addressLine1,
      addressLine2: profile.addressLine2 ?? null,
      city: profile.city,
      emirate: profile.emirate,
      status: profile.status ?? "Active",
      readinessMode: profile.readinessMode ?? "ReadinessOnly",
      accreditedServiceProviderConnectorId:
        profile.accreditedServiceProviderConnectorId ?? null,
      specificationIdentifier:
        profile.specificationIdentifier ?? "PINT-AE-READINESS",
      businessProcessType:
        profile.businessProcessType ?? "LEYLION-UAE-EINVOICE-READINESS",
      defaultCurrency: profile.defaultCurrency ?? "AED",
      defaultTimeZone: profile.defaultTimeZone ?? "Asia/Dubai",
      expectedVersion: profile.version ?? null
    })
  });
}

export async function getFiscalDocuments(): Promise<FiscalDocument[]> {
  return (await request<{ items: FiscalDocument[] }>(
    "/api/v1/compliance/uae/fiscal-documents?limit=100"
  )).items;
}

export function issueSimplifiedTaxInvoice(input: {
  buyerName?: string;
  descriptionAr: string;
  descriptionEn: string;
  quantity: number;
  unitPriceMinor: number;
  discountMinor: number;
  taxRate: number;
}): Promise<FiscalDocument> {
  return request<FiscalDocument>("/api/v1/compliance/uae/fiscal-documents", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      documentType: "SimplifiedTaxInvoice",
      orderId: null,
      customerId: null,
      buyerName: input.buyerName || null,
      buyerTrn: null,
      buyerElectronicAddress: null,
      buyerElectronicAddressSchemeId: null,
      buyerAddress: null,
      currency: "AED",
      timeZone: "Asia/Dubai",
      paymentMeansCode: "CASH",
      paymentDueDate: null,
      roundingAdjustmentMinor: 0,
      transactionTypeCode: "STANDARD_SALE",
      lines: [{
        productCode: null,
        descriptionAr: input.descriptionAr,
        descriptionEn: input.descriptionEn,
        quantity: input.quantity,
        unitCode: "EA",
        unitPriceMinor: input.unitPriceMinor,
        discountMinor: input.discountMinor,
        taxRate: input.taxRate,
        taxCategoryCode: input.taxRate > 0 ? "S" : "Z"
      }]
    })
  });
}


export function issueFiscalCreditNote(input: {
  originalDocumentId: string;
  creditReason: string;
  currency: string;
  timeZone: string;
  roundingAdjustmentMinor?: number;
  lines: Array<{
    productCode: string | null;
    descriptionAr: string;
    descriptionEn: string;
    quantity: number;
    unitCode: string;
    unitPriceMinor: number;
    discountMinor: number;
    taxRate: number;
    taxCategoryCode: string;
  }>;
}): Promise<FiscalDocument> {
  return request<FiscalDocument>("/api/v1/compliance/uae/credit-notes", {
    method: "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      ...input,
      roundingAdjustmentMinor: input.roundingAdjustmentMinor ?? 0
    })
  });
}

export function voidFiscalDocument(
  document: FiscalDocument,
  reason: string
): Promise<FiscalDocument> {
  return request<FiscalDocument>(
    `/api/v1/compliance/uae/fiscal-documents/${document.id}/void`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({ reason, expectedVersion: document.version })
    }
  );
}

export function updateFiscalTransmission(input: {
  document: FiscalDocument;
  status: FiscalDocument["transmissionStatus"];
  externalTransmissionId?: string | null;
  responseCode?: string | null;
  responseMessage?: string | null;
}): Promise<FiscalDocument> {
  return request<FiscalDocument>(
    `/api/v1/compliance/uae/fiscal-documents/${input.document.id}/transmission`,
    {
      method: "POST",
      headers: { "Idempotency-Key": createIdempotencyKey() },
      body: JSON.stringify({
        status: input.status,
        externalTransmissionId: input.externalTransmissionId ?? null,
        responseCode: input.responseCode ?? null,
        responseMessage: input.responseMessage ?? null,
        expectedVersion: input.document.version
      })
    }
  );
}

export async function getFiscalDocumentRevisions(
  documentId: string
): Promise<FiscalDocumentRevision[]> {
  return (await request<{ items: FiscalDocumentRevision[] }>(
    `/api/v1/compliance/uae/fiscal-documents/${documentId}/revisions`
  )).items;
}

export function getEInvoiceReadiness(documentId: string): Promise<{
  fiscalDocumentId: string;
  documentNumber: string;
  readinessMode: string;
  disclaimerAr: string;
  disclaimerEn: string;
  structuredPayload: Record<string, unknown>;
  payloadHashSha256: string;
  generatedAt: string;
}> {
  return request(`/api/v1/compliance/uae/fiscal-documents/${documentId}/einvoice-readiness`);
}

export async function getIntegrationSecrets(connectorId: string): Promise<IntegrationSecret[]> {
  return (await request<{ items: IntegrationSecret[] }>(
    `/api/v1/integrations/connectors/${connectorId}/secrets`
  )).items;
}

export function rotateIntegrationSecret(input: {
  connectorId: string;
  secretId?: string;
  name: string;
  purpose: IntegrationSecret["purpose"];
  value: string;
  version?: number;
}): Promise<IntegrationSecret> {
  const url = input.secretId
    ? `/api/v1/integrations/connectors/${input.connectorId}/secrets/${input.secretId}`
    : `/api/v1/integrations/connectors/${input.connectorId}/secrets`;
  return request<IntegrationSecret>(url, {
    method: input.secretId ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      name: input.name,
      purpose: input.purpose,
      value: input.value,
      expectedVersion: input.version ?? null
    })
  });
}

export async function getIntegrationDeviceBindings(): Promise<IntegrationDeviceBinding[]> {
  return (await request<{ items: IntegrationDeviceBinding[] }>(
    "/api/v1/integrations/device-bindings"
  )).items;
}

export function saveIntegrationDeviceBinding(input: {
  id?: string;
  connectorId: string;
  deviceProfileId?: string | null;
  terminalReference: string;
  merchantReference?: string | null;
  isEnabled: boolean;
  version?: number;
}): Promise<IntegrationDeviceBinding> {
  const url = input.id
    ? `/api/v1/integrations/device-bindings/${input.id}`
    : "/api/v1/integrations/device-bindings";
  return request<IntegrationDeviceBinding>(url, {
    method: input.id ? "PUT" : "POST",
    headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      connectorId: input.connectorId,
      deviceProfileId: input.deviceProfileId || null,
      terminalReference: input.terminalReference,
      merchantReference: input.merchantReference || null,
      isEnabled: input.isEnabled,
      expectedVersion: input.version ?? null
    })
  });
}

export function validateUaeTrn(trn: string): Promise<TrnValidation> {
  return request<TrnValidation>(
    `/api/v1/compliance/uae/trn/validate?trn=${encodeURIComponent(trn)}`
  );
}

export function getDataExchangeArtifact(jobId: string): Promise<DataExchangeArtifact> {
  return request<DataExchangeArtifact>(
    `/api/v1/integrations/data-exchange/jobs/${jobId}/artifact`
  );
}

export function getEInvoiceReadinessDocument(documentId: string): Promise<EInvoiceReadiness> {
  return request<EInvoiceReadiness>(
    `/api/v1/compliance/uae/fiscal-documents/${documentId}/einvoice-readiness`
  );
}


// Mobile, Tablet & Device Operations
export function getDeviceOperationsOverview(): Promise<DeviceOperationsOverview> {
  return request<DeviceOperationsOverview>("/api/v1/device-operations/overview");
}

export async function getDeviceEnrollments(): Promise<DeviceEnrollment[]> {
  return (await request<{ items: DeviceEnrollment[] }>("/api/v1/device-operations/enrollments")).items;
}

export function createDeviceEnrollment(input: {
  branchId: string; name: string; deviceClass: ManagedDeviceClass; platform?: ManagedDevicePlatform | null;
  allowedCapabilities: string[]; maxUses: number; expiresInMinutes: number;
}): Promise<DeviceEnrollment> {
  return request<DeviceEnrollment>("/api/v1/device-operations/enrollments", {
    method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify(input)
  });
}

export function revokeDeviceEnrollment(id: string, expectedVersion: number): Promise<DeviceEnrollment> {
  return request<DeviceEnrollment>(`/api/v1/device-operations/enrollments/${id}/revoke?expectedVersion=${expectedVersion}`, {
    method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }
  });
}

export async function getManagedDevices(): Promise<ManagedDevice[]> {
  return (await request<{ items: ManagedDevice[] }>("/api/v1/device-operations/devices")).items;
}

export function updateManagedDevice(device: ManagedDevice, patch: Partial<Pick<ManagedDevice, "name" | "deviceProfileId" | "locale" | "timeZone" | "capabilities" | "status">>): Promise<ManagedDevice> {
  return request<ManagedDevice>(`/api/v1/device-operations/devices/${device.id}`, {
    method: "PUT", headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({
      name: patch.name ?? device.name, deviceProfileId: patch.deviceProfileId ?? device.deviceProfileId ?? null,
      locale: patch.locale ?? device.locale, timeZone: patch.timeZone ?? device.timeZone,
      capabilities: patch.capabilities ?? device.capabilities, status: patch.status ?? device.status, expectedVersion: device.version
    })
  });
}

export function changeManagedDeviceStatus(device: ManagedDevice, status: Exclude<ManagedDeviceStatus, "Active" | "Wiped">): Promise<ManagedDevice> {
  const action = status === "Suspended" ? "suspend" : status === "Retired" ? "retire" : "wipe";
  return request<ManagedDevice>(`/api/v1/device-operations/devices/${device.id}/${action}?expectedVersion=${device.version}`, {
    method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }
  });
}

export async function getDeviceConfigurationProfiles(): Promise<DeviceConfigurationProfile[]> {
  return (await request<{ items: DeviceConfigurationProfile[] }>("/api/v1/device-operations/configuration-profiles")).items;
}

export function saveDeviceConfigurationProfile(input: Partial<DeviceConfigurationProfile> & { code: string; nameAr: string; nameEn: string; settings: Record<string, unknown> }): Promise<DeviceConfigurationProfile> {
  const url = input.id ? `/api/v1/device-operations/configuration-profiles/${input.id}` : "/api/v1/device-operations/configuration-profiles";
  return request<DeviceConfigurationProfile>(url, {
    method: input.id ? "PUT" : "POST", headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ branchId: input.branchId ?? null, code: input.code, nameAr: input.nameAr, nameEn: input.nameEn,
      deviceClass: input.deviceClass ?? null, settings: input.settings, priority: input.priority ?? 100,
      isActive: input.isActive ?? true, expectedVersion: input.version ?? null })
  });
}

export async function getDevicePeripherals(deviceId: string): Promise<DevicePeripheral[]> {
  return (await request<{ items: DevicePeripheral[] }>(`/api/v1/device-operations/devices/${deviceId}/peripherals`)).items;
}

export function saveDevicePeripheral(input: {
  deviceId: string; id?: string; code: string; name: string; peripheralType: DevicePeripheralType; transport: DeviceTransport;
  address?: string | null; vendor?: string | null; model?: string | null; configuration?: Record<string, unknown>;
  isDefault?: boolean; isEnabled?: boolean; version?: number;
}): Promise<DevicePeripheral> {
  const url = input.id
    ? `/api/v1/device-operations/devices/${input.deviceId}/peripherals/${input.id}`
    : `/api/v1/device-operations/devices/${input.deviceId}/peripherals`;
  return request<DevicePeripheral>(url, { method: input.id ? "PUT" : "POST", headers: { "Idempotency-Key": createIdempotencyKey() },
    body: JSON.stringify({ code: input.code, name: input.name, peripheralType: input.peripheralType, transport: input.transport,
      address: input.address ?? null, vendor: input.vendor ?? null, model: input.model ?? null, configuration: input.configuration ?? {},
      isDefault: input.isDefault ?? false, isEnabled: input.isEnabled ?? true, expectedVersion: input.version ?? null }) });
}

export function testDevicePeripheral(id: string, simulateSuccess = true): Promise<DevicePeripheral> {
  return request<DevicePeripheral>(`/api/v1/device-operations/peripherals/${id}/test`, {
    method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ simulateSuccess, errorCode: simulateSuccess ? null : "DEVICE_TEST_FAILED" })
  });
}

export function queueDeviceCommand(deviceId: string, commandType: DeviceCommandType, payload: Record<string, unknown> = {}): Promise<DeviceCommand> {
  return request<DeviceCommand>(`/api/v1/device-operations/devices/${deviceId}/commands`, {
    method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ commandType, payload, expiresInMinutes: 1440 })
  });
}

export function queueDevicePrintJob(deviceId: string, input: { externalReference: string; documentType: string; payload: unknown; copies?: number; devicePeripheralId?: string | null }): Promise<DevicePrintJob> {
  return request<DevicePrintJob>(`/api/v1/device-operations/devices/${deviceId}/print-jobs`, {
    method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({
      devicePeripheralId: input.devicePeripheralId ?? null, externalReference: input.externalReference, documentType: input.documentType,
      payload: input.payload, copies: input.copies ?? 1, maxAttempts: 5
    })
  });
}

export async function getOpenMobileSyncConflicts(): Promise<MobileSyncConflict[]> {
  return (await request<{ items: MobileSyncConflict[] }>("/api/v1/device-operations/sync/conflicts")).items;
}

export function resolveMobileSyncConflict(conflict: MobileSyncConflict, resolution: MobileSyncConflictStatus, mergedPayload?: unknown): Promise<MobileSyncConflict> {
  return request<MobileSyncConflict>(`/api/v1/device-operations/sync/conflicts/${conflict.id}/resolve`, {
    method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ resolution, mergedPayload: mergedPayload ?? null, expectedVersion: conflict.version })
  });
}


export function getProductionOverview(): Promise<ProductionOverview> {
  return request<ProductionOverview>("/api/v1/production-operations/overview");
}
export async function getCrossModuleWorkflows(status?: CrossModuleWorkflowStatus): Promise<CrossModuleWorkflow[]> {
  const query = status ? `?status=${status}&limit=200` : "?limit=200";
  return (await request<{ items: CrossModuleWorkflow[] }>(`/api/v1/production-operations/workflows${query}`)).items;
}
export function retryCrossModuleWorkflow(workflow: CrossModuleWorkflow, reason: string): Promise<CrossModuleWorkflow> {
  return request<CrossModuleWorkflow>(`/api/v1/production-operations/workflows/${workflow.id}/retry`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedVersion: workflow.version, reason }) });
}
export async function getReconciliationRuns(): Promise<ReconciliationRun[]> {
  return (await request<{ items: ReconciliationRun[] }>("/api/v1/production-operations/reconciliation/runs?limit=100")).items;
}
export function runReconciliation(scope: ReconciliationScope, from: string, to: string, autoQueueRepairs: boolean): Promise<ReconciliationRun> {
  return request<ReconciliationRun>("/api/v1/production-operations/reconciliation/runs", { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ scope, from, to, autoQueueRepairs }) });
}
export async function getReconciliationIssues(status?: ReconciliationIssueStatus, severity?: ReliabilitySeverity): Promise<ReconciliationIssue[]> {
  const params = new URLSearchParams({ limit: "300" }); if (status) params.set("status", status); if (severity) params.set("severity", severity);
  return (await request<{ items: ReconciliationIssue[] }>(`/api/v1/production-operations/reconciliation/issues?${params}`)).items;
}
export function resolveReconciliationIssue(issue: ReconciliationIssue, status: "Acknowledged" | "Resolved" | "Dismissed", resolutionNote: string): Promise<ReconciliationIssue> {
  return request<ReconciliationIssue>(`/api/v1/production-operations/reconciliation/issues/${issue.id}/resolve`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ status, resolutionNote, expectedVersion: issue.version }) });
}
export async function getRepairJobs(status?: RepairJobStatus): Promise<RepairJob[]> {
  const query = status ? `?status=${status}&limit=200` : "?limit=200";
  return (await request<{ items: RepairJob[] }>(`/api/v1/production-operations/repairs${query}`)).items;
}
export function queueRepairJob(input: { reconciliationIssueId?: string | null; jobType: RepairJobType; aggregateType: string; aggregateId: string; payload?: Record<string, unknown> }): Promise<RepairJob> {
  return request<RepairJob>("/api/v1/production-operations/repairs", { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ ...input, reconciliationIssueId: input.reconciliationIssueId ?? null, payload: input.payload ?? {} }) });
}
export function retryRepairJob(job: RepairJob): Promise<RepairJob> {
  return request<RepairJob>(`/api/v1/production-operations/repairs/${job.id}/retry`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedVersion: job.version }) });
}
export async function getOperationalIncidents(): Promise<OperationalIncident[]> {
  return (await request<{ items: OperationalIncident[] }>("/api/v1/production-operations/incidents?limit=200")).items;
}
export function acknowledgeOperationalIncident(incident: OperationalIncident, note: string): Promise<OperationalIncident> {
  return request<OperationalIncident>(`/api/v1/production-operations/incidents/${incident.id}/acknowledge`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ note, expectedVersion: incident.version }) });
}
export function resolveOperationalIncident(incident: OperationalIncident, resolutionNote: string): Promise<OperationalIncident> {
  return request<OperationalIncident>(`/api/v1/production-operations/incidents/${incident.id}/resolve`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ resolutionNote, expectedVersion: incident.version }) });
}
export async function getReliabilitySnapshots(): Promise<ServiceLevelSnapshot[]> {
  return (await request<{ items: ServiceLevelSnapshot[] }>("/api/v1/production-operations/reliability/snapshots?limit=100")).items;
}
export function captureReliabilitySnapshot(windowMinutes = 15): Promise<ServiceLevelSnapshot> {
  return request<ServiceLevelSnapshot>("/api/v1/production-operations/reliability/snapshots", { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ windowMinutes }) });
}
export async function getMaintenanceLeases(): Promise<MaintenanceLease[]> {
  return (await request<{ items: MaintenanceLease[] }>("/api/v1/production-operations/maintenance/leases")).items;
}
export function runProductionMaintenance(): Promise<MaintenanceCycle> {
  return request<MaintenanceCycle>("/api/v1/production-operations/maintenance/run", { method: "POST" });
}

export function getReleaseReadinessOverview(): Promise<ReleaseReadinessOverview> {
  return request<ReleaseReadinessOverview>("/api/v1/release-control/overview");
}
export async function getReleaseCandidates(): Promise<ReleaseCandidate[]> {
  return (await request<{ items: ReleaseCandidate[] }>("/api/v1/release-control/candidates?limit=50")).items;
}
export function createReleaseCandidate(input: { versionLabel: string; buildNumber: string; sourceFingerprint: string; manifestSha256: string; notes?: string | null }): Promise<ReleaseCandidate> {
  return request<ReleaseCandidate>("/api/v1/release-control/candidates", { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify(input) });
}
export function startReleaseVerification(candidate: ReleaseCandidate): Promise<ReleaseCandidate> {
  return request<ReleaseCandidate>(`/api/v1/release-control/candidates/${candidate.id}/verification/start`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedVersion: candidate.version }) });
}
export function rejectReleaseCandidate(candidate: ReleaseCandidate, reason: string): Promise<ReleaseCandidate> {
  return request<ReleaseCandidate>(`/api/v1/release-control/candidates/${candidate.id}/reject`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ reason, expectedVersion: candidate.version }) });
}
export async function getReleaseGates(candidateId: string): Promise<ReleaseGate[]> {
  return (await request<{ items: ReleaseGate[] }>(`/api/v1/release-control/candidates/${candidateId}/gates`)).items;
}
export function recordReleaseGate(candidateId: string, gate: ReleaseGate, status: ReleaseGateStatus, evidenceReference?: string): Promise<ReleaseGate> {
  return request<ReleaseGate>(`/api/v1/release-control/candidates/${candidateId}/gates/${gate.gateCode}`, { method: "PUT", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ status, evidenceReference: evidenceReference ?? null, details: { source: "release-control-center" }, expectedVersion: gate.version }) });
}
export function startReleaseUat(candidate: ReleaseCandidate): Promise<ReleaseCandidate> {
  return request<ReleaseCandidate>(`/api/v1/release-control/candidates/${candidate.id}/uat/start`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedVersion: candidate.version }) });
}
export async function getUatScenarios(): Promise<UatScenario[]> {
  return (await request<{ items: UatScenario[] }>("/api/v1/release-control/uat/scenarios")).items;
}
export async function getUatExecutions(candidateId: string): Promise<UatExecution[]> {
  return (await request<{ items: UatExecution[] }>(`/api/v1/release-control/candidates/${candidateId}/uat/executions`)).items;
}
export function executeUatScenario(candidateId: string, scenarioId: string, status: UatExecutionStatus, observedResult: string, expectedVersion?: number): Promise<UatExecution> {
  return request<UatExecution>(`/api/v1/release-control/candidates/${candidateId}/uat/scenarios/${scenarioId}`, { method: "PUT", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ status, observedResult, evidenceReference: null, expectedVersion: expectedVersion ?? null }) });
}
export async function getReleaseDefects(candidateId: string): Promise<ReleaseDefect[]> {
  return (await request<{ items: ReleaseDefect[] }>(`/api/v1/release-control/candidates/${candidateId}/defects`)).items;
}
export function createReleaseDefect(input: { releaseCandidateId: string; uatScenarioId?: string | null; module: string; title: string; reproductionSteps: string; expectedResult: string; actualResult: string; severity: ReleaseDefectSeverity }): Promise<ReleaseDefect> {
  return request<ReleaseDefect>("/api/v1/release-control/defects", { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify(input) });
}
export function updateReleaseDefect(defect: ReleaseDefect, status: ReleaseDefectStatus, resolutionNote?: string): Promise<ReleaseDefect> {
  return request<ReleaseDefect>(`/api/v1/release-control/defects/${defect.id}`, { method: "PUT", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ status, resolutionNote: resolutionNote ?? null, expectedVersion: defect.version }) });
}
export async function getReleaseSignoffs(candidateId: string): Promise<ReleaseSignoff[]> {
  return (await request<{ items: ReleaseSignoff[] }>(`/api/v1/release-control/candidates/${candidateId}/signoffs`)).items;
}
export function recordReleaseSignoff(candidateId: string, role: ReleaseSignoffRole, decision: ReleaseDecision, comments: string, expectedVersion?: number): Promise<ReleaseSignoff> {
  return request<ReleaseSignoff>(`/api/v1/release-control/candidates/${candidateId}/signoffs/${role}`, { method: "PUT", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ decision, comments, expectedVersion: expectedVersion ?? null }) });
}
export async function getReleaseArtifacts(candidateId: string): Promise<ReleaseArtifact[]> {
  return (await request<{ items: ReleaseArtifact[] }>(`/api/v1/release-control/candidates/${candidateId}/artifacts`)).items;
}
export function registerReleaseArtifact(candidateId: string, input: { artifactType: ReleaseArtifactType; name: string; storageReference: string; sha256: string; sizeBytes: number; metadata?: unknown }): Promise<ReleaseArtifact> {
  return request<ReleaseArtifact>(`/api/v1/release-control/candidates/${candidateId}/artifacts`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify(input) });
}
export async function getReleaseDeployments(candidateId: string): Promise<DeploymentRecord[]> {
  return (await request<{ items: DeploymentRecord[] }>(`/api/v1/release-control/candidates/${candidateId}/deployments`)).items;
}
export function createReleaseDeployment(input: { releaseCandidateId: string; environment: DeploymentEnvironment; imageReference: string; imageDigest: string; migrationVersion: string; rollbackVersion?: string | null }): Promise<DeploymentRecord> {
  return request<DeploymentRecord>("/api/v1/release-control/deployments", { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify(input) });
}
export function updateReleaseDeployment(deployment: DeploymentRecord, status: DeploymentStatus, failureReason?: string): Promise<DeploymentRecord> {
  return request<DeploymentRecord>(`/api/v1/release-control/deployments/${deployment.id}`, { method: "PUT", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ status, failureReason: failureReason ?? null, expectedVersion: deployment.version }) });
}

export interface CashierCouponOption {
  id: string; code: string; nameAr: string; nameEn: string; couponType: Coupon["couponType"];
  discountBasisPoints: number | null; amountMinor: number | null; bonusPoints: number | null; walletCreditMinor: number | null;
  currency: string; minimumOrderMinor: number; endsAt: string | null; isStackable: boolean;
  eligibility: { requiresCustomer?: boolean; isValid: boolean; invalidReason: string | null; discountMinor: number };
}
export function getCashierCoupons(orderId: string, search: string, offset = 0): Promise<{ items: CashierCouponOption[]; nextOffset: number | null }> {
  return request(`/api/v1/cashier/orders/${orderId}/coupons?${new URLSearchParams({ search, offset: String(offset) })}`);
}
export function removeCashierCoupon(orderId: string, expectedOrderVersion: number): Promise<OrderWorkflow> {
  return request(`/api/v1/cashier/orders/${orderId}/coupon/remove`, { method: "POST", headers: { "Idempotency-Key": createIdempotencyKey() }, body: JSON.stringify({ expectedOrderVersion }) });
}
export function reconcileCashierCoupon(orderId: string): Promise<OrderWorkflow> {
  return request(`/api/v1/cashier/orders/${orderId}/coupon/reconcile`, { method: "POST" });
}

export type DiningFloorInput = { id?: string; code: string; nameAr: string; nameEn: string; sortOrder: number; isActive: boolean; expectedVersion: number | null };
export type DiningTableInput = { id?: string; floorId: string; code: string; nameAr: string; nameEn: string; capacity: number; isActive: boolean; expectedVersion: number | null };
export async function getDiningConfiguration(): Promise<DiningFloor[]> {
  return (await request<{ items: DiningFloor[] }>("/api/v1/restaurant/dining")).items;
}
export function saveDiningFloor(input: DiningFloorInput, key: string): Promise<DiningFloor> {
  return request(`/api/v1/restaurant/dining/floors${input.id ? `/${input.id}` : ""}`, {
    method: input.id ? "PUT" : "POST", headers: { "Idempotency-Key": key }, body: JSON.stringify(input)
  });
}
export function saveDiningTable(input: DiningTableInput, key: string): Promise<import("./cashier-types").DiningTable> {
  return request(`/api/v1/restaurant/dining/tables${input.id ? `/${input.id}` : ""}`, {
    method: input.id ? "PUT" : "POST", headers: { "Idempotency-Key": key }, body: JSON.stringify(input)
  });
}


export function getCouponPrint(id: string): Promise<CouponPrint> { return request(`/api/v1/crm/coupons/${id}/print`); }
export function searchCouponProducts(search: string): Promise<CouponProduct[]> { return request<{ items: CouponProduct[] }>(`/api/v1/crm/coupons/products?${new URLSearchParams({ search })}`).then(x => x.items); }

export type GiftCardReplacement = { noExpiry: boolean; expiresAt: string | null };
export type GiftCardLookup = { id: string; giftCardNumber: string; currency: string; balanceMinor: number; status: import("./crm-types").GiftCardStatus; expiresAt: string | null; expired: boolean };
export function lookupGiftCard(orderId: string, code: string): Promise<GiftCardLookup> {
  return request(`/api/v1/cashier/orders/${orderId}/gift-card/lookup`, { method: "POST", body: JSON.stringify({ code }) });
}
export async function getRefundGiftCards(orderId: string, source: { refundId?: string; settlementId?: string }): Promise<GiftCard[]> {
  return (await request<{ items: GiftCard[] }>(`/api/v1/cashier/orders/${orderId}/refund-gift-cards?${new URLSearchParams(source)}`)).items;
}

export function previewCashierRefund(orderId: string, lines: Array<{ originalOrderLineId: string; quantityMilli: number }>): Promise<import("./cashier-types").RefundPreview> {
  return request(`/api/v1/cashier/orders/${orderId}/refund-preview`, { method: "POST", cache: "no-store", body: JSON.stringify({ lines }) });
}
export function getCashierRefundDocument(orderId: string, refundId: string): Promise<import("./cashier-types").RefundDocument> {
  return request(`/api/v1/cashier/orders/${orderId}/refunds/${refundId}/document`, { cache: "no-store" });
}
